import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomerDirectoryService } from '../../@services/customer-directory.service';
import { SoundPlayerService, SOUND_NOTES } from '../shared/sound-player.service';
import {
  AssetAuditRow,
  AssetShareCreateResponse,
  AssetSummary,
  AssetVisibility,
  CustomerAssetsService,
} from '../../@services/customer-assets.service';
import { ScannersService } from '../../@services/scanners.service';
import { ToastService } from '../../@services/toast.service';
import { ProductMapEditorComponent } from './product-map-editor.component';
import { environment as env } from '../../../environments/environment';
import { DateTime } from 'luxon';

/**
 * Super-admin customer directory panel. CRUD on F2-ADMIN.Customers
 * via the f2-admin-service2 customer-directory endpoints.
 *
 * Schema is permissive — fields below are the recommended shape, but
 * the user can add arbitrary JSON in the freeform editor.
 */
// Portal-handoff SSO config — mirrors SsoPortalConfig in f2-admin-service
// customer-directory.service.ts. Optional on the doc; only customers that
// integrate via the JWTSSO portal flow (PDF "Scanner SSO Integration Guide")
// need it. Shared HS256 secrets live IN the doc as `secrets[]` (encrypted
// at rest), managed via the Shared secrets panel rather than this form.
interface PermsSchemaField {
  key: string;
  label: string;
  type: 'string' | 'boolean' | 'enum';
  options?: string[];
  default?: string | boolean;
  help?: string;
}

interface ScannerSelection {
  mode: 'fixed' | 'from_jwt' | 'from_membership';
  scanner_claim?: string;
  membership_map?: Record<string, string>;
}

interface IdentityMapping {
  email?: string;
  email_template?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  // v2 additions (TheoTrade-style minimal payload)
  phone?: string;
  base64_fields?: string[];
}

interface SsoPortal {
  enabled?: boolean;
  expected_type?: string;
  required_claims?: string[];
  allowed_memberships?: string[];
  // F2-ADMIN.Scanners._id. The launch URL (host + path) comes from
  // F2-ADMIN.Scanners.Url; no separate target_scan_path field here.
  // Multi-scanner support is DERIVED from F2-ADMIN.Scanners where
  // Client === slug (source of truth); no scanners[] field here.
  scanner_id?: string;
  scanner_selection?: ScannerSelection;
  // Permission allowlist — subset of customer's owned scanners that SSO
  // can route into. Empty → all owned scanners are eligible.
  allowed_scanners?: string[];
  // Tells the validator where to find user identity in the JWT (used by
  // non-JWTSSO customers like TheoTrade with separate first_name/last_name).
  identity_mapping?: IdentityMapping;
  // v2 additions (TheoTrade-style minimal payload)
  optional_claims?: string[];
  allow_create_account_enabled?: boolean;
  max_iat_age_seconds?: number;
  // Wildcard-capable scanner allowlist applied to every SSO-onboarded user
  // (e.g. ['tt-*'] for TheoTrade grants all tt-* scanners on every grant).
  // Backend resolves wildcards against F2-ADMIN.Scanners where Client === slug.
  default_scanners?: string[];
  // Customer-supplied HTML returned on email-conflict auto-create failure
  // (supports hyperlinks, e.g. "<a href='mailto:support@x.com'>email us</a>").
  support_contact_html?: string;
  // Handoff URL shape the customer's SPA consumes:
  //   'sid' (default): opaque single-use 43-char SID in `?sid=`
  //   'token_query': F2-signed JWT in `?token=` (legacy / customer-symmetric)
  handoff_mode?: 'sid' | 'token_query';
  // Lifetime (seconds) of the F2-signed JWT in token_query handoff mode.
  // Default 3600 (1 hour). Not used when handoff_mode is 'sid'.
  token_query_exp_seconds?: number;
  // 'redirect' = top-level SSO navigation; 'iframe' = customer site
  // embeds our scanner. Both produce the same SID-handoff URL — this
  // declares supported modes for documentation + SSO Tester defaults.
  embed_modes?: ('iframe' | 'redirect')[];
  // Schema describing the JWT.perms object. Drives the SSO Tester popup
  // so operators get a guided form instead of typing free-text JSON.
  perms_schema?: PermsSchemaField[];
  // Template for the Cognito custom:claims entry written per user.
  // Values support {scanner_id}, {payload.*}, {perms.*} interpolation.
  // Empty/absent → default {scanner:'{scanner_id}', live_data:'true'}.
  claim_template?: Record<string, string>;
  sid_ttl_seconds?: number;
  // Iframe-refresh grace window: when > 0, F2 honors a second redeem of
  // the same sid within this many seconds (the first call no longer DELs
  // the Redis key — it shortens TTL via EXPIRE LT). Solves the
  // page-refresh-kicks-iframe-out failure where the parent re-mounts
  // the iframe with the same `?sid=` URL. Default 0 = strict GETDEL,
  // every existing customer's behavior unchanged.
  sid_redeem_grace_seconds?: number;
  docs_url?: string;
}

// Redacted secret summary (no plaintext / no encryption envelope) — what
// the listSsoSecrets endpoint returns.
interface SsoSecretSummary {
  id: string;
  fingerprint: string;
  length: number;
  label?: string;
  created_at: string;
  created_by_email?: string;
}

interface CustomerRecord {
  _id: string;
  name?: string;
  status?: 'active' | 'paused' | 'churned' | string;
  scanner_host?: string;
  // Top-level integration-guide pointer. Set for federated customers
  // (e.g. trend-labs riding MTA's SSO endpoint) that don't have an
  // sso_portal.docs_url because they don't carry an sso_portal at all.
  // Also a fallback for any customer who wants the docs link surfaced
  // even when their sso_portal config isn't enabled. Free-form URL or
  // path; consumers prepend env.apiUrl on leading-slash paths.
  docs_url?: string;
  // Public support email address for END USERS to contact when they
  // have login/account issues. Surfaced two places in f2-members when
  // set:
  //   1) A small "Need help? support@customer.com" line on the login
  //      page (and forgot-password / accept-invite mirrors).
  //   2) Appended as a mailto: link to the "This account has been
  //      disabled. Please contact support…" toast — one click drafts
  //      a pre-filled email so the user doesn't have to type from
  //      scratch. Empty/absent → surfaces nothing (unchanged behavior).
  // Distinct from the internal `contacts[]` roster (ops-only) and
  // from sso_portal.support_contact_html (raw HTML for a specific
  // email-conflict edge case). Mike 2026-07-15.
  support_email?: string;
  // Optional per-customer signature name used on commerce welcome emails
  // ("Thanks!\n<name>"). Falls back to `name` at the template. Hima
  // Reddy wants signoff as "Hima" not the full "HIMA Reddy" brand.
  email_signature_name?: string;
  domains?: string[];
  urls?: { label: string; url: string }[];
  customer_owned_domain?: boolean;
  github: { org: string; repos: string[] };
  vercel_projects?: string[];
  scanner_ids?: string[];
  contacts?: { role: string; name: string; email: string }[];
  notes?: string;
  exchange_agreements: { enforced: boolean; required: boolean };
  // Data-tier override. On the 'f2' doc this is the company-wide setting;
  // on other Customer docs it's the per-customer override. Both layers
  // are surfaced via brand-config (company_override + customer_override)
  // and consumed by f2tech-shared resolveDataTier(). Always present on
  // editor records — refresh() default-fills any missing value.
  // Editor uses 'off' sentinel for the no-override state; backend
  // _normalize_data_tier_override drops anything except the three real
  // modes, so 'off' persists as the field being absent (= no override).
  data_tier_override: { mode: 'delayed' | 'realtime' | 'disabled' | 'off' };
  // Per-customer alert sound played in /admin/error-reports on new live
  // events. Optional — when missing the player no-ops gracefully. Backend
  // brand-config builder pass-through (customer-directory.service.ts).
  error_alert_sound?: { enabled: boolean; instrument: string; note: string };
  // Per-customer auth-flow toggles. Surfaced via brand-config and read
  // by the f2 login page (and any self-hosted customer login).
  //
  // forgot_password_mode 2026-06-26: 3-way enum replacing the prior
  // boolean. Back-compat: brand-config still emits forgot_password_enabled
  // (= mode !== 'blocked') for any SPA on the old contract; new editor
  // writes the mode field exclusively.
  //   blocked     — no Forgot password? link shown
  //   cognito     — legacy 2-phase code-entry reset
  //   magic_link  — single email Send reset link path (requires SES-wired pool)
  auth?: {
    forgot_password_enabled?: boolean;
    forgot_password_mode?: 'blocked' | 'cognito' | 'magic_link';
    // Customer's own login page. When set, SSO-only per-product SPAs
    // (e.g. theo-trade → https://theotradelive.com/login) hard-nav here
    // on every login-required event — canActivate fail, validate-token
    // expiry, account-coin Sign Out, stale bookmark. Empty → SPA uses
    // the F2 login flow (legacy default). See runbook:external-login-url.
    external_login_url?: string;
  };
  // Per-customer dynamic-CSS branding for f2-members auth surfaces.
  // The editor surfaces ONE checkbox (`enabled`) — the rest of the
  // subdoc (accent_color, background_color, logo_asset_id, etc.) is
  // populated out-of-band (one-off Mongo dot-path $set when onboarding
  // a customer's brand). Toggling the checkbox lets Mike flip the
  // dynamic-CSS application on/off at will without losing the data.
  // Mike 2026-06-26: "I'd like a option on the customer source of truth
  // editor to apply customer css to ... so I can turn it off and on at
  // will." hima-reddy is the proof-through customer.
  branding?: {
    enabled?: boolean;
    accent_color?: string;
    accent_text_color?: string;
    background_color?: string;
    logo_asset_id?: string;
    logo_alt_text?: string;
    wordmark_asset_id?: string;
    wordmark_alt_text?: string;
    font_family?: string;
    wordmark_text?: string;
    tagline?: string;
    scope?: 'auth-only' | 'full';
  };
  // Customer-wide wildcard scanner access. When set to a future ISO
  // date, every user under this customer gets entitlement to every
  // scanner regardless of their custom:scanners. Empty / null / past
  // date = no wildcard, normal per-user entitlement applies. Checked
  // BEFORE custom:scanners by the per-product service's
  // _validate_scanner_access path.
  wildcard_access_until?: string | null;
  sso_portal?: SsoPortal;
  // Cross-customer opt-in for MTA's dedicated /rest/auth/validate-mta-token
  // endpoint. When true, MTA's portal can mint a JWT whose `scanner` claim
  // resolves (by Scanners._id or Scanners.aliases) to a scanner owned by
  // THIS customer, and the redeem lands on THIS customer's scanner host.
  // `slug === 'mta'` is implicitly allowed on its own endpoint and this
  // flag is not consulted for the MTA customer.
  mta_endpoint_allowed?: boolean;
  // Per-integration commerce config; secrets are stripped server-side
  // from the list projection. enabled is the only field the table row
  // needs to render the $-icon green-dot indicator.
  woocommerce?: { enabled?: boolean; default_scanner?: string; product_map?: Record<string, any> };
  stripe?: { enabled?: boolean; default_scanner?: string; product_map?: Record<string, any> };
  kartra?: { enabled?: boolean; default_scanner?: string; product_map?: Record<string, any> };
  samcart?: { enabled?: boolean; default_scanner?: string; product_map?: Record<string, any> };
  // IT-F2-378 cid 017e04ba — Keap panel parity with the other carts.
  // Backend adapter in f2-admin-service KeapController + KeapService.
  keap?: { enabled?: boolean; default_scanner?: string; product_map?: Record<string, any> };
  // IT-F2-392 c/e7dbfeac — Transactional email sender identity. Backend
  // reads from_address in customer-directory.service.ts:2462 and surfaces
  // it as auth.email_sender_from in /api/brand-config.
  //
  // IT-F2-392 clar b6d2815d option A — per-type sender split added
  // 2026-10-19 (Mike c/a8866f50, Alex's rule). support_from drives
  // welcome/notification sends; transactional_from drives magic-link,
  // forgot-password + invite sends. The legacy single from_address
  // stays as the per-slot fallback when the per-type field is empty.
  // All three admin-editable; SES must have each distinct address
  // verified as a sender identity out-of-band.
  email_sender?: {
    from_address?: string;
    support_from?: string;
    transactional_from?: string;
  };
  // IT-F2-391 Mike c/063a66a3 2026-10-23 — retention knob for the per-customer
  // audit log of sent notification-center alerts. NC audit worker purges
  // F2:NC_AUDIT entries older than this many days. Empty / 0 / absent → the
  // service default (90) applies. Keep as a lightweight int so operators can
  // dial retention per customer without a backend change.
  audit_retention_days?: number;
  created_at?: string | Date;
  updated_at?: string | Date;
  // Augmented by the backend list endpoint — aggregate scanner count
  // for this Customer._id across F2-ADMIN.Scanners.Client matches.
  scanner_count?: number;
  // Cached user counts from the F2:USER_CACHE Redis hash; null when
  // the admin hasn't clicked Refresh Client Counts yet. `recent` holds
  // a six-bucket histogram of users by their custom:last_login
  // attribute — buckets sum to `total`.
  client_count?: {
    active: number;
    inactive: number;
    total: number;
    recent?: { day: number; week: number; month: number; quarter: number; year: number; older: number };
  } | null;
  [k: string]: any;
}

const EMPTY_RECORD: CustomerRecord = {
  _id: '',
  name: '',
  status: 'active',
  scanner_host: '',
  domains: [],
  urls: [],
  customer_owned_domain: false,
  github: { org: 'F2Tech-WebDev-v2', repos: [] },
  vercel_projects: [],
  scanner_ids: [],
  contacts: [],
  notes: '',
  exchange_agreements: { enforced: true, required: true },
  data_tier_override: { mode: 'off' },
  auth: { forgot_password_enabled: true, external_login_url: '' },
  wildcard_access_until: null,
  sso_portal: undefined,
};

// Default sso_portal shape when an admin enables the section for the first
// time. Defaults align with the PDF "Scanner SSO Integration Guide" so a
// new SSO customer's form starts mostly-filled.
const DEFAULT_SSO_PORTAL: SsoPortal = {
  enabled: true,
  expected_type: 'JWTSSO',
  required_claims: ['email', 'name', 'memberships', 'perms', 'type', 'iat', 'exp'],
  allowed_memberships: [],
  scanner_id: '',
  scanner_selection: { mode: 'fixed' },
  identity_mapping: {},
  embed_modes: ['redirect'],
  perms_schema: [],
  claim_template: { scanner: '{scanner_id}', live_data: 'true' },
  sid_ttl_seconds: 90,
  docs_url: '',
};

@Component({
  selector: 'app-customer-directory',
  imports: [CommonModule, FormsModule, RouterLink, ProductMapEditorComponent],
  templateUrl: './customer-directory.component.html',
  styleUrl: './customer-directory.component.scss',
})
export class CustomerDirectoryComponent implements OnInit {
  records: CustomerRecord[] = [];
  loading = false;

  // Company-wide override mode read from the 'f2' Customer row. Drives
  // the visual greyout of the per-customer Exchange Agreements section
  // (when active, those per-customer toggles don't matter — the backend
  // forces enforced=false in brand-config). Returns null when no override
  // is active OR the f2 row hasn't loaded yet.
  get companyOverrideMode(): 'delayed' | 'realtime' | 'disabled' | null {
    const f2 = this.records.find(r => r._id === 'f2');
    const m = f2?.data_tier_override?.mode;
    return (m === 'delayed' || m === 'realtime' || m === 'disabled') ? m : null;
  }

  // Same shape as companyOverrideMode but reads the customer currently
  // in the editor. The radios use the 'off' sentinel for the no-override
  // state; this getter normalizes it back to null so the greyout logic
  // (and the inline "this customer's override is active" note) only
  // fires when a real override is set.
  get editingCustomerOverrideMode(): 'delayed' | 'realtime' | 'disabled' | null {
    const m = this.editing?.data_tier_override?.mode;
    return (m === 'delayed' || m === 'realtime' || m === 'disabled') ? m : null;
  }

  // Edit/create modal state
  showModal = false;
  isCreate = false;
  editing: CustomerRecord = { ...EMPTY_RECORD };
  reposText = '';
  vercelText = '';
  scannerIdsText = '';
  urlsJsonText = '';
  contactsJsonText = '';
  // In-progress hostname for the domain chip editor
  domainInput = '';
  // CSV bridges for sso_portal array fields (matches reposText / vercelText pattern)
  ssoPortalRequiredClaimsText = '';
  ssoPortalAllowedMembershipsText = '';
  // JSON-textarea bridges for the nested editors.
  ssoPortalMembershipMapJson = '{}';           // for scanner_selection.mode='from_membership'
  ssoPortalClaimTemplateJson = '{}';           // Record<string,string>, interpolated server-side

  // ----- SSO portal secret management (per open Customer edit) -----
  // Redacted list of active secrets for the customer currently in the modal.
  // Populated on openEdit; refreshed after generate / delete.
  ssoSecrets: SsoSecretSummary[] = [];
  ssoSecretsLoading = false;
  // One-time-reveal panel — when set, the UI shows the freshly-generated
  // secret in plaintext with a copy button + "I've saved it" confirm. After
  // dismissal, the value is gone (only fingerprint remains in ssoSecrets).
  revealedSecret: { id: string; secret: string; fingerprint: string; length: number; created_at: string } | null = null;

  // ----- Focused Portal Settings popup (per-row button on the table) -----
  // A friendlier surface for editing sso_portal fields without wading
  // through the full Customer edit modal. Same data, same save endpoint —
  // just laid out with plain-language sections, inline help, and
  // one-click presets so the operator never has to consult the PDF.
  showPortalSettings = false;
  portalSettingsRecord: CustomerRecord | null = null;
  // Same CSV bridges as the main edit modal so the form bindings work.
  psRequiredClaimsText = '';
  psAllowedMembershipsText = '';
  psMembershipMapJson = '{}';
  psClaimTemplateJson = '{}';
  // v2 CSV bridges
  psOptionalClaimsText = '';
  psBase64FieldsText = '';
  // Plaintext field for the "Import existing secret" form. Cleared on
  // successful import so the operator can't accidentally re-submit the
  // same value (also bounded by backend duplicate-fingerprint guard).
  importSecretPlaintext = '';
  // Label for the imported secret. Separate from revealSecretLabel
  // (which feeds Generate) so the Import form is fully self-contained
  // — operator doesn't need to scroll past the existing-secrets table
  // to find the label input.
  importSecretLabel = '';
  // default_scanners as a managed list (chips with delete + add picker).
  // Entries are either concrete scanner IDs from the customer's catalog
  // OR wildcard patterns ending in '*' (e.g. 'tt-*'). Wildcard expansion
  // happens at SSO grant time / read time (see backend userHasScanner).
  psDefaultScannersList: string[] = [];
  // In-progress entry the operator is typing in the wildcard-or-custom
  // input box. Empty when nothing being added.
  psDefaultScannersInput = '';
  // support_contact_html textarea bridge — customer-authored HTML returned
  // on email-conflict refusal (supports hyperlinks).
  psSupportContactHtml = '';
  revealSecretLabel = '';

  // ----- Customer Assets (per Portal Settings popup) -----
  // Asset list scoped to portalSettingsRecord._id. Refreshed on open,
  // on upload, on delete, on share create/revoke. Each row carries its
  // own audit-trail lazy-load buffer (loaded only when the operator
  // toggles the trail open).
  psAssets: AssetSummary[] = [];
  psAssetsLoading = false;
  // Upload form state. All scoped to the open popup so closing/reopening
  // resets them. File is staged in memory as base64 (data URL parsed).
  psAssetUploadFile: { name: string; type: string; size: number; base64: string } | null = null;
  psAssetUploadLabel = '';
  psAssetUploadVisibility: Record<AssetVisibility, boolean> = {
    admin: true,
    client_admin: true,
    member: true,
  };
  psAssetUploadCategory = 'sso-integration';
  // Per-asset expansion state for the audit trail. Map of asset_id ->
  // { open: boolean, loaded: boolean, rows: AssetAuditRow[] }. Lazy so
  // we don't burn audit queries on close-by-default.
  // Record entries are absent until the operator expands an asset's audit
  // panel; template uses `?.` so the type must allow undefined.
  psAssetAudit: Record<string, { open: boolean; loaded: boolean; loading: boolean; rows: AssetAuditRow[] } | undefined> = {};
  // Per-asset Create Share modal state. Active asset_id when the form
  // is open; null otherwise. The newly-minted plaintext token lives in
  // psRevealedShareToken — shown EXACTLY ONCE then dismissed.
  psShareFormAssetId: string | null = null;
  psShareFormLabel = '';
  psShareFormExpires = ''; // <input type="datetime-local"> value (yyyy-MM-ddTHH:mm)
  psRevealedShareToken: { assetId: string; token: string; share_url: string; full_url: string; label: string } | null = null;
  // Constants the template needs to reference.
  readonly ASSET_MAX_BYTES = CustomerAssetsService.MAX_BYTES;
  readonly ASSET_VISIBILITY_ROLES: AssetVisibility[] = ['admin', 'client_admin', 'member'];

  // ----- Payments & E-Commerce popup ($ icon) -----
  // Independent surface for per-customer commerce integration configs
  // (WooCommerce now; Stripe placeholder). Each integration owns:
  //   - enabled toggle
  //   - webhook secret (server-stored plaintext, hidden by default,
  //     reveal-on-demand, generate / paste / clear)
  //   - default_scanner (single-product fallback)
  //   - link out to any integration-guide assets already in
  //     F2-ADMIN.CustomerAssets for this customer + integration
  // Secret lifecycle is decoupled from non-secret save so a popup
  // refresh can never wipe a rotated secret.
  showCommerceSettings = false;
  commerceRecord: CustomerRecord | null = null;
  readonly COMMERCE_INTEGRATIONS: {
    key: string;
    label: string;
    ready: boolean;
    asset_category: string;
    // Exact webhook topic strings to select on the upstream side. Shown
    // under the Delivery URL row in the webhook editor so ops sees the
    // full setup checklist in one place. WC uses dot-delimited topic
    // names from a fixed dropdown; Kartra/SamCart's admin UIs use
    // action-event names -- pending confirmation before we surface
    // them here (leaving empty renders "any topic" instead of a stale
    // list).
    webhook_topics: string[];
    // Per-provider label for the shared-secret field. Each storefront
    // names it differently; matching the upstream vernacular removes a
    // translation step for the admin filling it in.
    //   WC       -> "Webhook secret"       (matches WC REST API UI)
    //   Kartra   -> "IPN Secret key"       (matches Kartra IPN Edit dialog)
    //   Stripe   -> "Signing secret"       (matches Stripe Webhook Settings)
    //   SamCart  -> "Webhook secret"       (matches SamCart Webhook Settings)
    secret_label: string;
  }[] = [
    { key: 'woocommerce', label: 'WooCommerce', ready: true,  asset_category: 'woocommerce-integration',
      webhook_topics: ['subscription.created', 'subscription.updated', 'subscription.deleted'],
      secret_label: 'Webhook secret' },
    { key: 'stripe',      label: 'Stripe',      ready: false, asset_category: 'stripe-integration',
      webhook_topics: [],
      secret_label: 'Signing secret' },
    { key: 'kartra',      label: 'Kartra',      ready: true,  asset_category: 'kartra-integration',
      webhook_topics: [],
      secret_label: 'IPN Secret key' },
    { key: 'samcart',     label: 'SamCart',     ready: true,  asset_category: 'samcart-integration',
      webhook_topics: [],
      secret_label: 'Webhook secret' },
    // IT-F2-378 cid 017e04ba — Keap panel. Backend MVP shipped by
    // f2-session (f2-admin-service PR #238, commit 9be1029). Reads
    // F2-ADMIN.Customers.<slug>.keap: {pat, webhook_secret, default_scanner, product_map}.
    //   webhook_secret → secret_field (standard pattern)
    //   pat            → consumer_secret_field (the only other long-lived
    //                    secret slot on this editor); on save we also
    //                    include body.pat so the backend can map to
    //                    keap.pat directly if that's the shape it wants.
    { key: 'keap',        label: 'Keap',        ready: true,  asset_category: 'keap-integration',
      webhook_topics: [],
      secret_label: 'Webhook secret' },
  ];
  commerceUi: Record<string, {
    enabled: boolean;
    default_scanner: string;
    secret_field: string;          // visible-or-input value
    secret_revealed: boolean;      // currently rendering plaintext?
    has_secret_stored: boolean;    // server reports a value?
    secret_dirty: boolean;         // user typed / pasted (needs save)
    // Where reachability "ping" deliveries get logged. One of
    // 'error_reports' (default) | 'commerce_events' | 'none'. Stored
    // on the customer doc under <integration>.ping_log.
    ping_log: 'error_reports' | 'commerce_events' | 'none';
    // Collapse toggle for the whole webhook setup block (Delivery URL,
    // Topics, Secret, Ping log, Ping stats). Closed by default because
    // once wired the values rarely need re-touching -- ops only cares
    // when they're setting the customer up or rotating the secret.
    // Companion to api_section_open below (same collapse UX).
    webhook_section_open: boolean;
    // Read-side REST API credentials for the merchant's WC store.
    // Used by the optional "WC API tools" expanded section that pulls
    // subscriptions/customers for reconciliation. Consumer secret
    // mirrors the webhook secret reveal pattern.
    api_section_open: boolean;     // expanded section toggle
    api_base_url: string;          // e.g. https://qpp-merchant.com
    consumer_key: string;          // ck_xxx (paired identifier)
    consumer_key_revealed: boolean; // hide by default; eye toggle
    consumer_secret_field: string; // visible-or-input value
    consumer_secret_revealed: boolean;
    has_consumer_secret_stored: boolean;
    consumer_secret_dirty: boolean;
    // Provider-specific API credentials nested under api_credentials.
    // Currently only Kartra populates these (needs app_id + api_key +
    // api_password); WC/SamCart use the shared consumer_key/secret slots
    // above. api_password mirrors consumer_secret's dirty-only-send +
    // has_stored + revealed pattern; app_id and api_key are non-secret
    // identifiers so they render plaintext (with copy button).
    api_credentials?: {
      app_id: string;
      app_id_revealed: boolean;
      app_id_dirty: boolean;
      api_key: string;
      api_key_revealed: boolean;
      api_key_dirty: boolean;
      api_password_field: string;
      api_password_revealed: boolean;
      has_api_password_stored: boolean;
      api_password_dirty: boolean;
    };
    // Transient — drives the Download button's spinner + prevents
    // parallel double-clicks. Not persisted or loaded from the server;
    // reset per-open of the popup.
    downloadingSubscriptions?: boolean;
    // IT-F2-432 c/ac99194c — SamCart API Token save-flow fix.
    // consumer_key_dirty is an explicit flag that flips on real
    // user input via onSamcartTokenChange, so the "unsaved" indicator
    // next to the API Token label stays truthful even when Chrome's
    // autofill prefills a stale value on open.
    consumer_key_dirty?: boolean;
    // Transient — drives the Test SamCart API button's spinner +
    // result pill. Not persisted; reset per popup open.
    testingSamcartToken?: boolean;
    samcartTokenTestResult?: {
      ok: boolean;
      message: string;
    };
  }> = {};

  // SamCart historical snapshots list (IT-F2-198 cid a73ce3bf). Loaded
  // from GET /commerce/samcart/snapshots when the Payments & E-Commerce
  // dialog opens on a customer with a stored SamCart token. Each row
  // is a complete / pending / failed snapshot the operator can download
  // or (next PR) feed into the dry-run reconcile viewer. Replaces the
  // old ad-hoc "Download subscriptions JSON" popup which used the
  // page-number pagination path that f2-session's PR#324 proved to be
  // a 100-sub trap.
  samcartSnapshots: Array<{
    _id?: string;
    run_id: string;
    customer_slug: string;
    status: 'pending' | 'complete' | 'failed';
    started_at: string;
    completed_at: string | null;
    subs_total: number;
    orders_total: number;
    subs_pages: number;
    orders_pages: number;
    customer_ids_total: number;
    emails_resolved: number;
    purchases_total: number;
    new_product_ids_added: string[];
    errors: string[];
  }> = [];
  samcartSnapshotsLoading = false;
  samcartSnapshotsLoadedForSlug: string | null = null;
  // UI-local flags for per-row save actions so each row's spinner is
  // independent of its siblings.
  samcartSnapshotRowBusy: Record<string, 'save' | 'save-raw' | null> = {};
  // Toggle on each Save .json click — true adds ?include=raw to the
  // snapshot fetch (operator-level audit payload). Default off per the
  // "lean by default" picks on cid 019bac88.
  samcartSaveIncludeRawPref = false;
  // Phase 2 (cid 59ceb9fe): one in-flight sync run at a time. Set on
  // POST /sync-historical return, cleared when the poller sees the row
  // transition to complete / failed. UI gates "Run new snapshot" on
  // null-ness and uses the pending row at top-of-list to display live
  // subs_total / orders_total / purchases_total as they advance.
  samcartActiveSyncRunId: string | null = null;
  private samcartActiveSyncPollTimer: any = null;
  // Last (subs_total, orders_total) tuple seen by the poller; when two
  // consecutive polls agree we're probably in a 429 backoff stall, so
  // back off the poll cadence from 3s → 10s per f2-session's suggestion
  // on cid a73ce3bf.
  private samcartActiveSyncLastProgress: { subs: number; orders: number } = { subs: 0, orders: 0 };
  private samcartActiveSyncIntervalMs = 3000;

  commerceAssets: AssetSummary[] = [];
  commerceAssetsLoading = false;
  // Snapshot of the loaded config for each integration so the popup
  // can offer a Revert button + warn on Close when there are unsaved
  // changes. Captured at the same point commerceUi is populated.
  commerceUiBaseline: Record<string, {
    enabled: boolean;
    default_scanner: string;
    ping_log: 'error_reports' | 'commerce_events' | 'none';
    api_base_url: string;
    consumer_key: string;
    product_map: Record<string, any>;
  }> = {};
  // Per-integration ping stats, sourced from the Customer doc on
  // popup open. Server-side ET-bucketed counters; refreshed on the
  // next popup open after each new ping arrives.
  commerceMostRecentPing: Record<string, string> = {};
  commercePingCounts: Record<string, { today: number; month: number; lifetime: number }> = {};
  // Live scanner catalog for the customer in the popup, used to render
  // the default_scanner dropdown with name + slug. Loaded on open via
  // ScannersService so the display names match the canonical row.
  // `disabled` mirrors the F2-ADMIN.Scanners.RoleAccess === 'disabled'
  // status so the dropdown can red-tag retired scanners.
  commerceScannerCatalog: Array<{ id: string; name: string; disabled: boolean }> = [];
  // IT-F2-136 v2 (2026-08-03): full scanner docs keyed by scanner id,
  // needed by the grants drawer to resolve per-scanner claims_schema +
  // Channels[]. Populated alongside commerceScannerCatalog at popup
  // open — same source (F2-ADMIN.Scanners for the current customer)
  // but preserves every field instead of projecting to {id,name,disabled}.
  commerceScannerDocs: Record<string, any> = {};
  // Sentinel value for the "Members page" default_scanner option —
  // routes the user to the customer's members page rather than granting
  // a specific scanner. Server-side: WC adapter treats this as "no
  // specific scanner", which delegates to client_activate_user's
  // empty-scanner path (grants the customer's full catalog).
  readonly COMMERCE_MEMBERS_PAGE = '__members__';
  // Wildcard sentinel for the product_map. Same backend semantics as
  // members-page (no-scanner activate -> grant full catalog); separate
  // label in the dropdown so the operator's intent stays distinct.
  readonly COMMERCE_WILDCARD = '*';
  // Per-integration editable rows for the product_map. Persisted back
  // to a {wc_product_id: target} object on save. Empty wc_product_id
  // rows are ignored on save. Default target on row-add = '' (falls
  // through to default_scanner at WC-event time).
  commerceProductMapRows: Record<string, Array<{ wc_product_id: string; target: string; __meta?: any }>> = {};
  // { [integ.key]: { [wc_product_id]: product_name } } derived from the
  // customer's cached wc-subscriptions-<slug>-<YYYY-MM-DD> collection.
  // Fed to <app-product-map-editor> so the editor renders a "Product
  // name" column next to the numeric id -- ops speaks the customer's
  // product-catalog language instead of memorizing WC ids.
  commerceProductNames: Record<string, Record<string, string>> = {};
  // Expanded/collapsed state per integration for the product-map
  // editor. Auto-opens on load when there's already a stored map.
  commerceProductMapOpen: Record<string, boolean> = {};

  // ----- Email Provider (Customer.io) section state -----
  // Lives on the same popup as WC, but is its own card (sibling
  // section). One config per customer (NOT per integration).
  commerceEmailSectionOpen = false;
  commerceEmailUi: {
    enabled: boolean;
    kind: string;
    send_via: string;
    site_id: string;
    segment_id: string;
    attribute_name: string;
    triggers_text: string;            // CSV bridge for triggers_on_scanners
    api_key_field: string;
    api_key_revealed: boolean;
    has_api_key_stored: boolean;
    api_key_dirty: boolean;
  } | null = null;
  commerceEmailBaseline: {
    enabled: boolean; kind: string; send_via: string; site_id: string; segment_id: string;
    attribute_name: string;
    triggers_text: string;
  } | null = null;

  // Cross-integration commerce settings. Two flags — apply to every
  // commerce webhook path (WC / Stripe / Kartra / SamCart / partner-API):
  //   auto_welcome_email    — LEGACY. Toggle Cognito's built-in temp-
  //                            password welcome email (qpp / delphi-sig).
  //   welcome_email_enabled — NEW 2026-07-15. Toggle F2-sent branded
  //                            welcome (magic-link for new users;
  //                            plain "subscription active" landing at
  //                            <customer.domains[0]>/scans/<scanner>
  //                            for existing users). Leave OFF for
  //                            SSO/OEM tenants whose users never
  //                            interact with F2's login/member surface.
  commerceSharedUi: { auto_welcome_email: boolean; welcome_email_enabled: boolean } | null = null;
  commerceSharedBaseline: { auto_welcome_email: boolean; welcome_email_enabled: boolean } | null = null;

  // Delete confirmation
  showDeleteConfirm = false;
  deleteTarget: CustomerRecord | null = null;

  constructor(
    private svc: CustomerDirectoryService,
    private assetsSvc: CustomerAssetsService,
    private scannersSvc: ScannersService,
    private toast: ToastService,
    private soundPlayer: SoundPlayerService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  // Note options bound to the alert-sound picker dropdown — four octaves
  // covering the practical range of the synth envelopes.
  readonly soundNotes = SOUND_NOTES;

  /**
   * Ensure editing.error_alert_sound exists before any picker control
   * mutates one of its fields. Called from the enable checkbox + the
   * instrument / note selects so the object is created lazily — keeps
   * the Mongo doc clean for customers who never touch this section.
   */
  _ensure_error_alert_sound() {
    if (!this.editing) return;
    if (!this.editing.error_alert_sound) {
      this.editing.error_alert_sound = {
        enabled: false,
        instrument: 'acoustic_grand_piano',
        note: 'C5',
      } as any;
    }
  }

  /** Audition the picker's current selection. Bypasses the brand-config cache. */
  previewAlertSound() {
    this._ensure_error_alert_sound();
    const s = this.editing.error_alert_sound!;
    this.soundPlayer.preview(s.instrument || 'acoustic_grand_piano', s.note || 'C5');
  }

  async ngOnInit() {
    await this.refresh();
    // Deep-link from /admin/services Commerce Connections section.
    // ?commerce=<customer_id> auto-opens that customer's commerce
    // settings modal once the records list is hydrated. The param is
    // then stripped from the URL so a reload doesn't re-pop the modal.
    const commerceId = this.route.snapshot.queryParamMap.get('commerce');
    if (commerceId) {
      const record = this.records.find(r => r._id === commerceId);
      if (record) {
        this.openCommerceSettings(record);
      } else {
        this.toast.error('Commerce', `Customer not found: ${commerceId}`);
      }
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { commerce: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  // Timestamp of the most recent backend client-count recomputation.
  // null until the admin clicks Refresh Client Counts.
  clientCountsComputedAt: string | Date | null = null;

  async refresh() {
    this.loading = true;
    const res: any = await this.svc.list();
    const records = Array.isArray(res) ? res : [];
    // Default-fill the editor-managed sub-objects BEFORE storing so any
    // subsequent openEdit can never see undefined — Angular's [(ngModel)]
    // silently no-ops when the LHS dereferences through undefined, which
    // produces empty checkboxes and a hidden override section even when
    // the rest of the form binds correctly.
    for (const r of records) {
      if (!r.exchange_agreements) r.exchange_agreements = { enforced: true, required: true };
      // 'off' sentinel for the no-override state — Angular radio binding
      // doesn't reliably tick a [value]="null" option.
      if (!r.data_tier_override || r.data_tier_override.mode == null) {
        r.data_tier_override = { mode: 'off' };
      }
      // editing.github.org is bound directly in the template (line ~267);
      // when github is missing on a Customer doc the binding throws a
      // runtime TypeError and KILLS the rest of the template render below
      // it — including the data_tier_override + exchange_agreements
      // sections. Default-fill so the binding always has an object.
      if (!r.github) r.github = { org: 'F2Tech-WebDev-v2', repos: [] };
      // Auth-flow toggles -- default to enabled so the table cell and
      // the editor checkbox both render a real value for legacy
      // customer docs that predate the `auth` block.
      if (!r.auth) r.auth = { forgot_password_enabled: true };
      else if (typeof r.auth.forgot_password_enabled !== 'boolean') {
        r.auth.forgot_password_enabled = true;
      }
      // Normalize wildcard date to ISO string for consistent comparison
      // + binding to <input type="date"> in the editor.
      if (r.wildcard_access_until) {
        try { r.wildcard_access_until = new Date(r.wildcard_access_until).toISOString(); }
        catch { r.wildcard_access_until = null; }
      } else {
        r.wildcard_access_until = null;
      }
    }
    this.records = records;
    // Backend stamps the array with a non-enumerable computed_at field
    // when it has cached counts. Read defensively for forward-compat
    // with the future {docs, computed_at} response shape.
    this.clientCountsComputedAt = (res && (res as any).client_counts_computed_at) || null;
    this.loading = false;
  }

  async refresh_client_counts() {
    const res = await this.svc.refreshClientCounts();
    if (res && !res.err) {
      // After the heavy recompute, re-fetch the list so each row sees
      // its updated client_count.
      await this.refresh();
    }
  }

  fmtDate(d: any): string {
    if (!d) return '';
    try {
      return DateTime.fromJSDate(new Date(d)).setZone('America/New_York').toFormat('LLL d yyyy, HH:mm');
    } catch {
      return String(d);
    }
  }

  // Date-only display for the wildcard-access expiration column.
  // Returns 'MMM d, yyyy' in America/New_York so the column matches how
  // an admin would naturally read an expiration ("expires Oct 14, 2026").
  fmtDateOnly(d: any): string {
    if (!d) return '';
    try {
      return DateTime.fromJSDate(new Date(d)).setZone('America/New_York').toFormat('LLL d, yyyy');
    } catch {
      return String(d);
    }
  }

  // True when the customer's wildcard_access_until is a parseable date
  // that has NOT yet passed. Drives the grid badge color (green=active,
  // red=expired) and is what the per-product service should mirror
  // server-side before short-circuiting custom:scanners.
  isWildcardActive(d: any): boolean {
    if (!d) return false;
    const t = new Date(d).getTime();
    return Number.isFinite(t) && t > Date.now();
  }

  // <input type="date"> binds to yyyy-MM-dd local-time strings, not ISO.
  // Read the ISO value as a local date string for the picker.
  wildcardInputValue(): string {
    const v = this.editing?.wildcard_access_until;
    if (!v) return '';
    try {
      return DateTime.fromJSDate(new Date(v)).setZone('America/New_York').toFormat('yyyy-LL-dd');
    } catch { return ''; }
  }

  // Date picker writes yyyy-MM-dd; interpret as end-of-day America/New_York
  // so the wildcard is active for the WHOLE selected day in the trading
  // tz, then persist as ISO. Empty string clears the field.
  setWildcardFromInput(value: string) {
    if (!this.editing) return;
    const s = (value || '').trim();
    if (!s) {
      this.editing.wildcard_access_until = null;
      return;
    }
    try {
      const dt = DateTime.fromFormat(s, 'yyyy-LL-dd', { zone: 'America/New_York' }).endOf('day');
      this.editing.wildcard_access_until = dt.isValid ? dt.toUTC().toISO() : null;
    } catch { this.editing.wildcard_access_until = null; }
  }

  openCreate() {
    this.isCreate = true;
    this.editing = JSON.parse(JSON.stringify(EMPTY_RECORD));
    if (!this.editing.auth) this.editing.auth = { forgot_password_enabled: true };
    if (!this.editing.auth.forgot_password_mode) this.editing.auth.forgot_password_mode = 'cognito';
    this.reposText = '';
    this.vercelText = '';
    this.scannerIdsText = '';
    this.domainInput = '';
    this.urlsJsonText = '[]';
    this.contactsJsonText = '[]';
    this.ssoPortalRequiredClaimsText = '';
    this.ssoPortalAllowedMembershipsText = '';
    this.ssoPortalMembershipMapJson = '{}';
    this.ssoPortalClaimTemplateJson = '{}';
    this.ssoSecrets = [];
    this.revealedSecret = null;
    this.revealSecretLabel = '';
    this.showModal = true;
  }

  get forgotPasswordModeValue(): 'blocked' | 'cognito' | 'magic_link' {
    const raw = this.editing?.auth?.forgot_password_mode;
    if (raw === 'blocked' || raw === 'cognito' || raw === 'magic_link') return raw;
    return this.editing?.auth?.forgot_password_enabled === false ? 'blocked' : 'cognito';
  }

  setForgotPasswordMode(v: string) {
    if (!this.editing.auth) this.editing.auth = { forgot_password_enabled: true };
    const mode = (v === 'blocked' || v === 'cognito' || v === 'magic_link') ? v : 'cognito';
    this.editing.auth.forgot_password_mode = mode;
    this.editing.auth.forgot_password_enabled = mode !== 'blocked';
  }

  openEdit(record: CustomerRecord) {
    this.isCreate = false;
    this.editing = JSON.parse(JSON.stringify(record));
    this.editing.domains = this.editing.domains || [];
    // Default missing section so the checkboxes have something to bind to.
    // Mirrors the backend's missing-field default in getBrandConfig().
    this.editing.exchange_agreements = this.editing.exchange_agreements || { enforced: true, required: true };
    this.editing.data_tier_override = this.editing.data_tier_override || { mode: null };
    if (!this.editing.auth) this.editing.auth = { forgot_password_enabled: true };
    else if (typeof this.editing.auth.forgot_password_enabled !== 'boolean') {
      this.editing.auth.forgot_password_enabled = true;
    }
    // IT-F2-407 (Mike 2026-09-18): default-fill forgot_password_mode so the
    // dropdown always has a concrete value bound. Prior template used
    // [ngModel]="editing.auth?.forgot_password_mode || (...fallback...)"
    // — a display-only expression that computes to a valid option, BUT
    // Angular's <select> binding needs the OPTION `value` to match the
    // underlying model exactly for it to be rendered. On a customer doc
    // without forgot_password_mode set, the computed fallback returned
    // 'cognito' as a display value but the actual model was undefined,
    // and the dropdown rendered empty because 'cognito' option's value
    // matched a nullish binding after the first ngModelChange cycle
    // wired the raw undefined back through the two-way form ref. Force
    // the field onto the model so the dropdown always reflects the
    // canonical state.
    if (!this.editing.auth.forgot_password_mode) {
      this.editing.auth.forgot_password_mode = this.editing.auth.forgot_password_enabled === false
        ? 'blocked'
        : 'cognito';
    }
    // Default-fill external_login_url so the save payload always carries
    // the key. Without this, JSON.stringify would drop `undefined` and
    // the backend's $set: {auth: {...}} would silently wipe an
    // externally-set URL — the wipes-siblings class documented in
    // feedback_set_object_replaces_wipes_siblings.
    if (this.editing.auth.external_login_url === undefined) {
      this.editing.auth.external_login_url = '';
    }
    // Default-fill so the date picker / clear button always see a real
    // key (legacy docs predate the field). null is the sentinel the
    // backend $unset path looks for.
    if (this.editing.wildcard_access_until === undefined) this.editing.wildcard_access_until = null;
    this.reposText = (this.editing.github?.repos || []).join(', ');
    this.vercelText = (this.editing.vercel_projects || []).join(', ');
    this.scannerIdsText = (this.editing.scanner_ids || []).join(', ');
    this.domainInput = '';
    this.urlsJsonText = JSON.stringify(this.editing.urls ?? [], null, 2);
    this.contactsJsonText = JSON.stringify(this.editing.contacts ?? [], null, 2);
    this.ssoPortalRequiredClaimsText = (this.editing.sso_portal?.required_claims || []).join(', ');
    this.ssoPortalAllowedMembershipsText = (this.editing.sso_portal?.allowed_memberships || []).join(', ');
    this.ssoPortalMembershipMapJson = JSON.stringify(this.editing.sso_portal?.scanner_selection?.membership_map || {}, null, 2);
    this.ssoPortalClaimTemplateJson = JSON.stringify(this.editing.sso_portal?.claim_template || {}, null, 2);
    this.ssoSecrets = [];
    this.revealedSecret = null;
    this.revealSecretLabel = '';
    // Customer-level asset list (SharePoint-style attachments) — always
    // loaded regardless of sso_portal because federated customers also
    // need to attach docs. Separate from psAssets (which is the Portal
    // SSO popup's richer view).
    this.customerAssets = [];
    this.customerAssetUploadFile = null;
    this.customerAssetUploading = false;
    if (record._id) this._loadCustomerAssetsForEdit(record._id);
    this.showModal = true;
    // Lazy-load existing secrets when the customer has sso_portal seeded.
    if (record._id && record.sso_portal) {
      this._loadSsoSecrets(record._id);
    }
  }

  // Toggle the SSO portal section on/off. Enabling pre-fills the form
  // with PDF-aligned defaults (JWTSSO type, standard 7-claim list, 90s
  // sid TTL); disabling drops the sub-object entirely so the Customer
  // doc stays clean (Mongo is schemaless — absent field = no SSO).
  toggleSsoPortal() {
    if (this.editing.sso_portal) {
      this.editing.sso_portal = undefined;
      this.ssoPortalRequiredClaimsText = '';
      this.ssoPortalAllowedMembershipsText = '';
      this.ssoPortalMembershipMapJson = '{}';
      this.ssoPortalClaimTemplateJson = '{}';
    } else {
      this.editing.sso_portal = JSON.parse(JSON.stringify(DEFAULT_SSO_PORTAL));
      this.ssoPortalRequiredClaimsText = (DEFAULT_SSO_PORTAL.required_claims || []).join(', ');
      this.ssoPortalAllowedMembershipsText = (DEFAULT_SSO_PORTAL.allowed_memberships || []).join(', ');
      this.ssoPortalMembershipMapJson = '{}';
      this.ssoPortalClaimTemplateJson = JSON.stringify(DEFAULT_SSO_PORTAL.claim_template || {}, null, 2);
    }
  }

  // ----- allowed_scanners (permission) -----
  // Checkbox per scanner id the customer owns. The pool is the
  // editing.scanner_ids[] array (already maintained on the Customer doc).
  isScannerAllowed(scannerId: string): boolean {
    return !!this.editing.sso_portal?.allowed_scanners?.includes(scannerId);
  }
  toggleScannerAllowed(scannerId: string) {
    if (!this.editing.sso_portal) return;
    const cur = this.editing.sso_portal.allowed_scanners || [];
    if (cur.includes(scannerId)) {
      this.editing.sso_portal.allowed_scanners = cur.filter(s => s !== scannerId);
    } else {
      this.editing.sso_portal.allowed_scanners = [...cur, scannerId];
    }
  }
  // Owned-scanner list reflects the CSV input so admins see live edits
  // immediately without losing in-progress state.
  get ownedScannerIds(): string[] {
    return this.scannerIdsText.split(',').map(s => s.trim()).filter(Boolean);
  }

  // ----- Portal Settings popup -----
  // Tooltip for the key icon. Green = at least one portal-side pathway is
  // wired (own sso_portal, or federated onto MTA's endpoint, or is MTA).
  // Called from the template — a helper keeps the [title] binding readable.
  _portalIconTitle(r: CustomerRecord): string {
    const bits: string[] = [];
    if (r.sso_portal) bits.push('Portal SSO handoff configured');
    if (r._id === 'mta') bits.push('Owns /rest/auth/validate-mta-token');
    else if (r.mta_endpoint_allowed) bits.push('Allowed on MTA endpoint');
    return bits.length ? 'Portal SSO settings — ' + bits.join(', ') : 'Configure portal SSO';
  }

  openPortalSettings(record: CustomerRecord) {
    // Deep-clone so cancel discards changes cleanly.
    this.portalSettingsRecord = JSON.parse(JSON.stringify(record));
    const sp = this.portalSettingsRecord!.sso_portal;
    this.psRequiredClaimsText = (sp?.required_claims || []).join(', ');
    this.psAllowedMembershipsText = (sp?.allowed_memberships || []).join(', ');
    this.psMembershipMapJson = JSON.stringify(sp?.scanner_selection?.membership_map || {}, null, 2);
    this.psClaimTemplateJson = JSON.stringify(sp?.claim_template || {}, null, 2);
    this.psOptionalClaimsText = (sp?.optional_claims || []).join(', ');
    this.psBase64FieldsText = (sp?.identity_mapping?.base64_fields || []).join(', ');
    this.psDefaultScannersList = Array.isArray(sp?.default_scanners) ? [...sp!.default_scanners!] : [];
    this.psDefaultScannersInput = '';
    this.psSupportContactHtml = sp?.support_contact_html || '';
    // Default handoff_mode to 'sid' so the dropdown ngValue match
    // resolves cleanly on customers whose doc predates this field.
    if (sp && sp.handoff_mode !== 'token_query') sp.handoff_mode = 'sid';
    this.ssoSecrets = [];
    this.revealedSecret = null;
    this.revealSecretLabel = '';
    this.showPortalSettings = true;
    if (record._id && record.sso_portal) this._loadSsoSecrets(record._id);
    // Customer-assets surface always loads on open, even when sso_portal
    // is currently disabled — operators may want to attach docs in
    // anticipation of enabling SSO later.
    this._resetAssetUploadForm();
    this.psAssets = [];
    this.psAssetAudit = {};
    this.psShareFormAssetId = null;
    this.psRevealedShareToken = null;
    if (record._id) this._loadCustomerAssets(record._id);
  }
  closePortalSettings() {
    this.showPortalSettings = false;
    this.portalSettingsRecord = null;
    this.psAssets = [];
    this.psAssetAudit = {};
    this.psShareFormAssetId = null;
    this.psRevealedShareToken = null;
    this._resetAssetUploadForm();
  }

  // ----- Payments & E-Commerce popup ($ icon) -----
  async openCommerceSettings(record: CustomerRecord) {
    this.commerceRecord = JSON.parse(JSON.stringify(record));
    this.commerceUi = {};
    for (const integ of this.COMMERCE_INTEGRATIONS) {
      this.commerceUi[integ.key] = {
        enabled: false,
        default_scanner: '',
        secret_field: '',
        secret_revealed: false,
        has_secret_stored: false,
        secret_dirty: false,
        ping_log: 'error_reports',
        webhook_section_open: false,
        api_section_open: false,
        api_base_url: '',
        consumer_key: '',
        consumer_key_revealed: false,
        consumer_secret_field: '',
        consumer_secret_revealed: false,
        has_consumer_secret_stored: false,
        consumer_secret_dirty: false,
        api_credentials: {
          app_id: '',
          app_id_revealed: false,
          app_id_dirty: false,
          api_key: '',
          api_key_revealed: false,
          api_key_dirty: false,
          api_password_field: '',
          api_password_revealed: false,
          has_api_password_stored: false,
          api_password_dirty: false,
        },
      };
    }
    this.commerceMostRecentPing = {};
    this.commercePingCounts = {};
    this.commerceAssets = [];
    this.commerceScannerCatalog = [];
    this.commerceScannerDocs = {};
    this.commerceProductMapRows = {};
    this.commerceProductMapOpen = {};
    this.commerceProductNames = {};
    // IMPORTANT: do NOT show the popup before getCommerce returns.
    // If we did, the operator could click Save while the async fetch
    // is still in flight -- commerceSave would serialize the empty
    // commerceUi defaults (including an empty product_map) and the
    // PUT would $set product_map: {} on the customer doc, wiping the
    // stored mappings. Defer showCommerceSettings until hydration is
    // complete so the Save can never operate on un-hydrated state.
    if (!record._id) {
      this.showCommerceSettings = true;
      return;
    }
    const cfg: any = await this.svc.getCommerce(record._id);
    if (cfg && !cfg.err) {
      for (const integ of this.COMMERCE_INTEGRATIONS) {
        const c = cfg[integ.key] || {};
        const validPingLog = (c.ping_log === 'none' || c.ping_log === 'commerce_events' || c.ping_log === 'error_reports')
          ? c.ping_log : 'error_reports';
        this.commerceUi[integ.key] = {
          enabled: !!c.enabled,
          default_scanner: c.default_scanner || '',
          secret_field: '',
          secret_revealed: false,
          has_secret_stored: !!c.has_secret,
          secret_dirty: false,
          ping_log: validPingLog,
          // Always start collapsed -- per Mike, secondary sections shouldn't
          // pre-expand even when populated. Operator clicks chevron to open.
          webhook_section_open: false,
          api_section_open: false,
          api_base_url: c.api_base_url || '',
          consumer_key: c.consumer_key || '',
          consumer_key_revealed: false,
          consumer_secret_field: '',
          consumer_secret_revealed: false,
          has_consumer_secret_stored: !!c.has_consumer_secret,
          consumer_secret_dirty: false,
          api_credentials: {
            // app_id / api_key come through the safe projection as
            // plaintext (they're identifiers, not secrets). api_password
            // is redacted server-side and only its presence is exposed
            // via has_api_password so we can render a "stored" indicator.
            // Bracket notation on the api_credentials subdoc because
            // the response is typed with an index signature; TS4111
            // rejects dot-access on those without the bracket form.
            app_id: (c.api_credentials as any)?.['app_id'] || '',
            app_id_revealed: false,
            app_id_dirty: false,
            api_key: (c.api_credentials as any)?.['api_key'] || '',
            api_key_revealed: false,
            api_key_dirty: false,
            api_password_field: '',
            api_password_revealed: false,
            has_api_password_stored: !!(c.api_credentials as any)?.['has_api_password'],
            api_password_dirty: false,
          },
        };
        this.commerceUiBaseline[integ.key] = {
          enabled: !!c.enabled,
          default_scanner: c.default_scanner || '',
          ping_log: validPingLog,
          api_base_url: c.api_base_url || '',
          consumer_key: c.consumer_key || '',
          product_map: { ...(c.product_map || {}) },
        };
        if (c.most_recent_ping_at) {
          this.commerceMostRecentPing[integ.key] = String(c.most_recent_ping_at);
        }
        this.commercePingCounts[integ.key] = {
          today: Number(c.ping_count_today) || 0,
          month: Number(c.ping_count_month) || 0,
          lifetime: Number(c.ping_count_lifetime) || 0,
        };
        this._hydrateProductMapRows(integ.key, c.product_map || {});
      }
    }
    // All hydrated -- now safe to show the popup. Subsequent async
    // loads (assets, scanner catalog) are purely informational and
    // don't gate Save correctness.
    this.showCommerceSettings = true;
    // Reset the snapshot list so stale rows from another customer
    // don't flash before the fresh fetch lands.
    this.samcartSnapshots = [];
    this.samcartSnapshotsLoadedForSlug = null;
    this.samcartSnapshotRowBusy = {};
    // Fire-and-forget — the SamCart card has its own loading state.
    void this.loadSamcartSnapshots('samcart');
    this.commerceAssetsLoading = true;
    try {
      this.commerceAssets = await this.assetsSvc.list(record._id);
    } finally {
      this.commerceAssetsLoading = false;
    }
    // Load the scanner catalog for the dropdown.
    try {
      const all: any = await this.scannersSvc.getScanners();
      const arr: any[] = Array.isArray(all) ? all : (all?.err ? [] : []);
      const forCustomer = arr.filter((s) => String(s?.Client || '') === record._id);
      this.commerceScannerCatalog = forCustomer
        .map((s) => ({
          id: String(s.Id || s._id || ''),
          name: String(s.Name || s.Id || s._id || ''),
          disabled: String(s.RoleAccess || '') === 'disabled',
        }))
        .filter((s) => s.id);
      // IT-F2-136 v2: full docs keyed by id for the grants drawer.
      const docs: Record<string, any> = {};
      for (const s of forCustomer) {
        const id = String(s.Id || s._id || '');
        if (id) docs[id] = s;
      }
      this.commerceScannerDocs = docs;
    } catch {
      this.commerceScannerCatalog = [];
      this.commerceScannerDocs = {};
    }
    // Load the WC product-name map (nice-to-have context for the
    // product-map editor's Product Name column). Silent on failure --
    // the editor falls back to just showing numeric ids.
    try {
      this.commerceProductNames['woocommerce'] = await this.svc.getWcProductNames(record._id);
    } catch {
      this.commerceProductNames['woocommerce'] = {};
    }
    // Load the email-provider config for this customer. It's one
    // config per customer (NOT per integration), so it lives on the
    // popup as a sibling section to the integration cards. Failure
    // here is non-blocking -- the rest of the popup stays usable.
    this.commerceEmailUi = null;
    this.commerceEmailBaseline = null;
    this.commerceEmailSectionOpen = false;
    try {
      const ep: any = await this.svc.getEmailProvider(record._id);
      if (ep && !ep.err) {
        const triggers: string[] = Array.isArray(ep.triggers_on_scanners)
          ? ep.triggers_on_scanners.map((s: any) => String(s || '').trim()).filter(Boolean)
          : [];
        const triggers_text = triggers.join(', ');
        this.commerceEmailUi = {
          enabled: !!ep.enabled,
          kind: String(ep.kind || 'customer_io'),
          send_via: String(ep.send_via || 'customer_io'),
          site_id: String(ep.site_id || ''),
          segment_id: String(ep.segment_id || ''),
          attribute_name: String(ep.attribute_name || ''),
          triggers_text,
          api_key_field: '',
          api_key_revealed: false,
          has_api_key_stored: !!ep.has_api_key,
          api_key_dirty: false,
        };
        this.commerceEmailBaseline = {
          enabled: !!ep.enabled,
          kind: String(ep.kind || 'customer_io'),
          send_via: String(ep.send_via || 'customer_io'),
          site_id: String(ep.site_id || ''),
          segment_id: String(ep.segment_id || ''),
          attribute_name: String(ep.attribute_name || ''),
          triggers_text,
        };
      }
    } catch { /* leave email UI null on failure */ }

    // Load the cross-integration commerce settings (auto_welcome_email
    // etc.). Non-blocking — if this fails the checkbox just won't render
    // until the next popup open.
    this.commerceSharedUi = null;
    this.commerceSharedBaseline = null;
    try {
      const cs: any = await this.svc.getCommerceSettings(record._id);
      if (cs && !cs.err) {
        this.commerceSharedUi = {
          auto_welcome_email: !!cs.auto_welcome_email,
          welcome_email_enabled: !!cs.welcome_email_enabled,
        };
        this.commerceSharedBaseline = {
          auto_welcome_email: !!cs.auto_welcome_email,
          welcome_email_enabled: !!cs.welcome_email_enabled,
        };
      }
    } catch { /* leave shared UI null on failure */ }
  }

  // Hydrate the product_map rows for one integration from stored map.
  // Handles both shapes (IT-F2-89 2026-07-24):
  //   * legacy string form:  { "1094998": "op-fingerprints" }
  //   * object form:         { "1094998": { scanner, name, description?,
  //                                          first_seen_at, last_seen_at } }
  // Object entries feed commerceProductNames[integ] so the editor's
  // "Cart Product Name" column renders — no separate WC-subscriptions
  // fetch needed for SamCart / Kartra (they carry names in the map
  // itself, populated at webhook time by the auto-insert flow).
  // Rows also carry a __meta stash so serialize can round-trip the
  // description/first_seen_at/last_seen_at fields untouched.
  private _hydrateProductMapRows(integ: string, stored: Record<string, any>) {
    const rows: Array<{ wc_product_id: string; target: string; __meta?: any }> = [];
    const names = this.commerceProductNames[integ] || {};
    for (const [pid, entry] of Object.entries(stored || {})) {
      const key = String(pid);
      if (entry && typeof entry === 'object') {
        rows.push({
          wc_product_id: key,
          target: String((entry as any).scanner ?? ''),
          __meta: entry,
        });
        if ((entry as any).name) names[key] = String((entry as any).name);
      } else {
        rows.push({ wc_product_id: key, target: String(entry) });
      }
    }
    this.commerceProductNames[integ] = names;
    rows.sort((a, b) => (Number(a.wc_product_id) || 0) - (Number(b.wc_product_id) || 0));
    this.commerceProductMapRows[integ] = rows;
    // Always start collapsed -- per Mike, secondary sections shouldn't
    // pre-expand even when populated; the operator opens them when they
    // need to edit. Reduces vertical scroll for the common no-edit open.
    this.commerceProductMapOpen[integ] = false;
  }

  closeCommerceSettings() {
    if (this.commerceHasAnyDirty()) {
      const ok = (typeof window !== 'undefined' && (window as any).confirm)
        ? (window as any).confirm('You have unsaved commerce changes. Close anyway and lose them?')
        : true;
      if (!ok) return;
    }
    this.showCommerceSettings = false;
    this.commerceRecord = null;
    this.commerceUi = {};
    this.commerceUiBaseline = {};
    this.commerceAssets = [];
    this.commerceScannerCatalog = [];
    this.commerceScannerDocs = {};
    this._pastBuyerCountsProviderCache.clear();
    this.commerceMostRecentPing = {};
    // Cancel any in-flight snapshot poller + drop stale snapshot state
    // so re-opening a different customer doesn't flash the previous
    // customer's rows.
    this._samcartClearPoll();
    this.samcartActiveSyncRunId = null;
    this.samcartSnapshots = [];
    this.samcartSnapshotsLoadedForSlug = null;
    this.samcartSnapshotRowBusy = {};
    this.commercePingCounts = {};
    this.commerceProductMapRows = {};
    this.commerceProductMapOpen = {};
    this.commerceEmailUi = null;
    this.commerceEmailBaseline = null;
    this.commerceEmailSectionOpen = false;
    this.commerceSharedUi = null;
    this.commerceSharedBaseline = null;
  }

  // ----- product_map row editor -----
  commerceProductMapAdd(integ: string) {
    if (!this.commerceProductMapRows[integ]) this.commerceProductMapRows[integ] = [];
    this.commerceProductMapRows[integ].push({ wc_product_id: '', target: '' });
  }
  commerceProductMapRemove(integ: string, index: number) {
    const rows = this.commerceProductMapRows[integ];
    if (!rows) return;
    rows.splice(index, 1);
  }
  // Serialize the rows to the map shape the backend stores. IT-F2-89
  // 2026-07-24: preserves the object shape for rows that carried a
  // __meta stash on hydrate (name / description / first_seen_at /
  // last_seen_at survive the round-trip), and emits plain-string
  // entries for rows the operator adds by hand (no meta captured yet;
  // backend's auto-insert flow will upgrade them on next webhook).
  // Skips rows where wc_product_id is empty. Last write wins on
  // duplicate pids (operator can see the dup in the list).
  private _serializeProductMap(integ: string): Record<string, any> {
    const rows = this.commerceProductMapRows[integ] || [];
    const out: Record<string, any> = {};
    for (const r of rows as Array<{ wc_product_id: string; target: string; __meta?: any }>) {
      const pid = String(r.wc_product_id || '').trim();
      const t = String(r.target || '').trim();
      if (!pid) continue;
      if (r.__meta && typeof r.__meta === 'object') {
        out[pid] = { ...r.__meta, scanner: t };
      } else {
        out[pid] = t;
      }
    }
    return out;
  }
  // Cheap deep-equal for the product maps (either shape) so dirty
  // detection doesn't false-positive on key-order swaps. For object
  // entries, compares canonicalized scanner + name only (metadata
  // like last_seen_at drift is not a user-visible dirty signal).
  private _productMapsEqual(a: Record<string, any>, b: Record<string, any>): boolean {
    const ak = Object.keys(a).sort();
    const bk = Object.keys(b).sort();
    if (ak.length !== bk.length) return false;
    const canon = (v: any) => {
      if (v && typeof v === 'object') return String(v.scanner ?? '') + '|' + String(v.name ?? '');
      return String(v ?? '') + '|';
    };
    for (let i = 0; i < ak.length; i++) {
      if (ak[i] !== bk[i]) return false;
      if (canon(a[ak[i]]) !== canon(b[bk[i]])) return false;
    }
    return true;
  }

  // True iff any user-editable field for this integration drifted
  // from the baseline captured at popup-open. Covers enabled,
  // default_scanner, ping_log, AND the secret_dirty flag (which
  // flips on paste / typing -- the secret value itself isn't tracked
  // in the baseline because plaintext doesn't live on the popup
  // until reveal).
  commerceIsDirty(integ: string): boolean {
    const ui = this.commerceUi[integ];
    const base = this.commerceUiBaseline[integ];
    if (!ui || !base) return false;
    if (ui.enabled !== base.enabled) return true;
    if (ui.default_scanner !== base.default_scanner) return true;
    if (ui.ping_log !== base.ping_log) return true;
    if (ui.secret_dirty) return true;
    if (ui.api_base_url !== base.api_base_url) return true;
    // IT-F2-432 c/ac99194c — explicit dirty flag on consumer_key handles
    // the Chrome-autofill-wipe case where the input visually shows the
    // typed value but ui.consumer_key hasn't been updated by the
    // [(ngModel)] binding. onSamcartTokenChange keeps the flag honest.
    if (ui.consumer_key_dirty) return true;
    if (ui.consumer_key !== base.consumer_key) return true;
    if (ui.consumer_secret_dirty) return true;
    if (!this._productMapsEqual(this._serializeProductMap(integ), base.product_map)) return true;
    return false;
  }

  commerceHasAnyDirty(): boolean {
    for (const integ of this.COMMERCE_INTEGRATIONS) {
      if (this.commerceIsDirty(integ.key)) return true;
    }
    if (this.commerceEmailIsDirty()) return true;
    if (this.commerceSharedIsDirty()) return true;
    return false;
  }

  commerceSharedIsDirty(): boolean {
    const ui = this.commerceSharedUi;
    const base = this.commerceSharedBaseline;
    if (!ui || !base) return false;
    return ui.auto_welcome_email !== base.auto_welcome_email
      || ui.welcome_email_enabled !== base.welcome_email_enabled;
  }

  async commerceSharedSave() {
    if (!this.commerceRecord?._id || !this.commerceSharedUi) return;
    const res: any = await this.svc.updateCommerceSettings(this.commerceRecord._id, {
      auto_welcome_email: this.commerceSharedUi.auto_welcome_email,
      welcome_email_enabled: this.commerceSharedUi.welcome_email_enabled,
    });
    if (res && !res.err) {
      this.commerceSharedBaseline = {
        auto_welcome_email: this.commerceSharedUi.auto_welcome_email,
        welcome_email_enabled: this.commerceSharedUi.welcome_email_enabled,
      };
    }
  }

  commerceSharedRevert() {
    if (!this.commerceSharedUi || !this.commerceSharedBaseline) return;
    this.commerceSharedUi.auto_welcome_email = this.commerceSharedBaseline.auto_welcome_email;
    this.commerceSharedUi.welcome_email_enabled = this.commerceSharedBaseline.welcome_email_enabled;
  }

  // Restore the popup state for one integration to the snapshot we
  // took on open. Does NOT call the server; secret field is cleared
  // so the user has to reveal again to see what's stored.
  commerceRevert(integ: string) {
    const ui = this.commerceUi[integ];
    const base = this.commerceUiBaseline[integ];
    if (!ui || !base) return;
    if (!this.commerceIsDirty(integ)) return;
    const ok = (typeof window !== 'undefined' && (window as any).confirm)
      ? (window as any).confirm('Discard unsaved changes for this integration?')
      : true;
    if (!ok) return;
    ui.enabled = base.enabled;
    ui.default_scanner = base.default_scanner;
    ui.ping_log = base.ping_log;
    ui.secret_field = '';
    ui.secret_revealed = false;
    ui.secret_dirty = false;
    ui.api_base_url = base.api_base_url;
    ui.consumer_key = base.consumer_key;
    ui.consumer_key_revealed = false;
    ui.consumer_secret_field = '';
    ui.consumer_secret_revealed = false;
    ui.consumer_secret_dirty = false;
    this._hydrateProductMapRows(integ, base.product_map);
  }

  // Toggle consumer_key visibility. The key isn't a server-side
  // secret (returned in plaintext by getCommerce) so no async reveal
  // call -- just flip the input type between password and text.
  // ---- Kartra api_credentials: reveal/paste/clear on app_id + api_key.
  // Mirrors the consumer_secret UX (reveal toggle + copy + paste + clear)
  // but for the two identifier fields that come through plaintext from
  // the server. api_password uses the separate consumer_secret-style
  // reveal already wired inline in the template.
  kartraCredToggleReveal(integ: string, field: 'app_id' | 'api_key' | 'api_password') {
    const ui = this.commerceUi[integ];
    const ac = ui?.api_credentials;
    if (!ac) return;
    if (field === 'app_id') ac.app_id_revealed = !ac.app_id_revealed;
    else if (field === 'api_key') ac.api_key_revealed = !ac.api_key_revealed;
    else if (field === 'api_password') ac.api_password_revealed = !ac.api_password_revealed;
  }

  async kartraCredPaste(integ: string, field: 'app_id' | 'api_key' | 'api_password') {
    const ui = this.commerceUi[integ];
    const ac = ui?.api_credentials;
    if (!ac) return;
    try {
      const text = (await navigator.clipboard.readText() || '').trim();
      if (field === 'app_id') {
        ac.app_id = text;
        ac.app_id_revealed = true;
        ac.app_id_dirty = true;
      } else if (field === 'api_key') {
        ac.api_key = text;
        ac.api_key_revealed = true;
        ac.api_key_dirty = true;
      } else if (field === 'api_password') {
        ac.api_password_field = text;
        ac.api_password_revealed = true;
        ac.api_password_dirty = true;
      }
    } catch (e: any) {
      this.toast.error('Paste failed', e?.message || 'Clipboard read blocked');
    }
  }

  kartraCredClear(integ: string, field: 'app_id' | 'api_key' | 'api_password') {
    const ui = this.commerceUi[integ];
    const ac = ui?.api_credentials;
    if (!ac) return;
    if (field === 'app_id') {
      ac.app_id = '';
      ac.app_id_revealed = false;
      ac.app_id_dirty = true;
    } else if (field === 'api_key') {
      ac.api_key = '';
      ac.api_key_revealed = false;
      ac.api_key_dirty = true;
    } else if (field === 'api_password') {
      ac.api_password_field = '';
      ac.api_password_revealed = false;
      ac.api_password_dirty = true;
    }
  }

  commerceConsumerKeyToggleReveal(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui) return;
    ui.consumer_key_revealed = !ui.consumer_key_revealed;
  }

  copyToClipboard(s: string | null | undefined) {
    if (!s) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(s).then(
        () => this.toast.success('Copied', s.length > 60 ? s.slice(0, 60) + '…' : s),
        (e: any) => this.toast.error('Copy failed', e?.message || 'Clipboard write blocked'),
      );
    }
  }

  // ---- consumer secret reveal / paste / clear ----
  async commerceConsumerSecretToggleReveal(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui || !this.commerceRecord?._id) return;
    if (ui.consumer_secret_revealed) {
      ui.consumer_secret_revealed = false;
      return;
    }
    if (ui.consumer_secret_field) {
      ui.consumer_secret_revealed = true;
      return;
    }
    if (!ui.has_consumer_secret_stored) {
      ui.consumer_secret_revealed = true;
      return;
    }
    const res: any = await this.svc.revealCommerceConsumerSecret(this.commerceRecord._id, integ);
    if (res?.err) return;
    ui.consumer_secret_field = String(res.secret || '');
    ui.consumer_secret_revealed = true;
  }

  async commerceConsumerSecretCopy(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui || !this.commerceRecord?._id) return;
    let value = ui.consumer_secret_field;
    if (!value && ui.has_consumer_secret_stored) {
      const res: any = await this.svc.revealCommerceConsumerSecret(this.commerceRecord._id, integ);
      if (res?.err) return;
      value = String(res.secret || '');
      ui.consumer_secret_field = value;
      ui.consumer_secret_revealed = true;
    }
    if (!value) return;
    this.copyToClipboard(value);
  }

  async commerceConsumerSecretPaste(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui) return;
    try {
      const text = await navigator.clipboard.readText();
      ui.consumer_secret_field = (text || '').trim();
      ui.consumer_secret_revealed = true;
      ui.consumer_secret_dirty = true;
    } catch (e: any) {
      this.toast.error('Paste failed', e?.message || 'Clipboard read blocked');
    }
  }

  commerceConsumerSecretClear(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui) return;
    ui.consumer_secret_field = '';
    ui.consumer_secret_revealed = false;
    ui.consumer_secret_dirty = true; // signal that the save body should drop the value
    // Actual delete server-side happens via the standard updateCommerce
    // PUT call sending consumer_secret="" (handled in the service).
  }

  onConsumerSecretUserInput(integ: string, ev?: KeyboardEvent) {
    const ui = this.commerceUi[integ];
    if (!ui) return;
    if (ev && 'key' in ev) {
      const k = String(ev.key || '');
      const noop = new Set(['Shift','Control','Alt','Meta','CapsLock','Tab','Escape','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','PageUp','PageDown']);
      if (noop.has(k)) return;
      if ((ev.ctrlKey || ev.metaKey) && k.toLowerCase() === 'c') return;
    }
    ui.consumer_secret_dirty = true;
  }

  // ---- WC API: sync subscriptions -> F2 entitlement ----
  async commerceReconcileWcSubscriptions(integ: string) {
    if (integ !== 'woocommerce') return;
    if (!this.commerceRecord?._id) return;
    const ui = this.commerceUi[integ];
    if (!ui?.api_base_url || !ui?.consumer_key || (!ui.has_consumer_secret_stored && !ui.consumer_secret_field)) {
      this.toast.error('Missing credentials', 'Save the API base URL + consumer key/secret first.');
      return;
    }
    const ok = (typeof window !== 'undefined' && (window as any).confirm)
      ? (window as any).confirm('Reconcile WC subscriptions to F2 entitlement?\n\nFor each WC subscription, F2 will: create missing users, add scanners for active subs, remove scanners for non-active subs, and add the customer slug to existing users that don\'t have it. Every action is logged to /admin/commerce-events.')
      : true;
    if (!ok) return;
    const res = await this.svc.reconcileWcSubscriptions(this.commerceRecord._id);
    if (!res?.ok) return;
    const s = res.summary || {};
    const a = s.actions || {};
    const summary = `${s.distinct_emails || 0} email(s) across ${s.total_subs || 0} sub(s). ` +
      `Created ${a.create_user || 0}, activated ${a.activate || 0}, revoked ${a.revoke || 0}, ` +
      `added slug ${a.add_customer_slug || 0}, noop ${a.noop || 0}, unmapped ${a.unmapped || 0}, errors ${a.error || 0}.`;
    // Use successWithSupportId so the summary lands in /admin/error-reports
    // with a Support ID -- support can paste the ID to look up the exact
    // run details (counts, run_id, errors) instead of trying to recall
    // what the toast said.
    const diagnostics: any = {
      error_code: (a.error || 0) > 0 ? 'F2ADMIN_RECONCILE_E01' : 'F2ADMIN_RECONCILE_INFO_E01',
      customer: this.commerceRecord._id,
      reconcile_run_id: s.reconcile_run_id,
      total_subs: s.total_subs,
      distinct_emails: s.distinct_emails,
      action_create_user: a.create_user,
      action_activate: a.activate,
      action_revoke: a.revoke,
      action_add_customer_slug: a.add_customer_slug,
      action_noop: a.noop,
      action_unmapped: a.unmapped,
      action_error: a.error,
      first_errors: Array.isArray(s.errors) ? JSON.stringify(s.errors.slice(0, 3)) : undefined,
    };
    if ((a.error || 0) > 0) {
      this.toast.error('Reconcile completed with errors', summary, diagnostics);
    } else {
      await this.toast.successWithSupportId('Reconcile complete', summary, diagnostics);
    }
  }

  // IT-F2-432 c/ac99194c — Explicit value-capture handler for the SamCart
  // API Token input. The field used to be [(ngModel)] with a type="password"
  // / type="text" toggle that let Chrome's autofill overlay path win on
  // paste in a sibling customer's popup (Mike re-saw his option-pit token
  // disappear while editing). (ngModelChange) now lands here on every
  // real keystroke / paste, trims whitespace, and flips an explicit
  // consumer_key_dirty flag so the Save button indicator can't lie.
  onSamcartTokenChange(integ: string, v: string | null | undefined) {
    const ui = this.commerceUi[integ];
    if (!ui) return;
    const next = String(v ?? '').trim();
    ui.consumer_key = next;
    const base = this.commerceUiBaseline[integ]?.consumer_key ?? '';
    ui.consumer_key_dirty = next !== base;
    // Clear a stale test-result pill whenever the token changes — the
    // probe below is point-in-time and shouldn't claim "ok" for a key
    // that no longer matches what's in the input.
    if (ui.samcartTokenTestResult) ui.samcartTokenTestResult = undefined;
  }

  // IT-F2-432 c/5e429a80 — "Do we have a test button for it in the api
  // form?". Fires a lightweight auth probe against SamCart with the
  // CURRENT in-input token (not the stored one), so operators can
  // verify a freshly-pasted token BEFORE committing to a Save that
  // runs product-map remap sweeps. Backend: /commerce/samcart/test
  // (added in f2-admin-service companion PR) accepts an optional
  // `token` body param and probes /v1/me — returns {ok, status,
  // account_email?} without persisting anything.
  async commerceTestSamcartToken(integ: string) {
    if (integ !== 'samcart') return;
    if (!this.commerceRecord?._id) return;
    const ui = this.commerceUi[integ];
    if (!ui) return;
    const token = (ui.consumer_key || '').trim();
    if (!token) {
      ui.samcartTokenTestResult = { ok: false, message: 'No token entered.' };
      return;
    }
    ui.testingSamcartToken = true;
    ui.samcartTokenTestResult = undefined;
    try {
      const res: any = await this.svc.testSamcartToken(this.commerceRecord._id, { token });
      if (res?.ok) {
        const acct = res.account_email ? ` (${res.account_email})` : '';
        ui.samcartTokenTestResult = {
          ok: true,
          message: `Authenticated · HTTP ${res.status || 200}${acct}`,
        };
      } else {
        ui.samcartTokenTestResult = {
          ok: false,
          message: res?.message || res?.err || `Probe failed · HTTP ${res?.status || '?'}`,
        };
      }
    } catch (e: any) {
      ui.samcartTokenTestResult = { ok: false, message: e?.message || 'Probe threw' };
    } finally {
      ui.testingSamcartToken = false;
    }
  }

  // SamCart sibling — sync subscriptions to F2 entitlement.
  async commerceReconcileSamcartSubscriptions(integ: string) {
    if (integ !== 'samcart') return;
    if (!this.commerceRecord?._id) return;
    const ui = this.commerceUi[integ];
    // SamCart uses a single API token; reuses the WC consumer_key slot
    // so the API-creds editor is shared. consumer_secret is unused.
    if (!ui?.consumer_key) {
      this.toast.error('Missing credentials', 'Save the SamCart API token first.');
      return;
    }
    const ok = (typeof window !== 'undefined' && (window as any).confirm)
      ? (window as any).confirm('Reconcile SamCart subscriptions to F2 entitlement?\n\nFor each SamCart subscription, F2 will: create missing users, add scanners for active subs, remove scanners for non-active subs. Every action is logged to /admin/commerce-events.')
      : true;
    if (!ok) return;
    const res = await this.svc.reconcileSamcartSubscriptions(this.commerceRecord._id);
    if (!res?.ok) return;
    const summary = `${res.emails_seen || 0} email(s) across ${res.subscriptions_fetched || 0} sub(s). ` +
      `Activated ${res.activated || 0}, deactivated ${res.deactivated || 0}, errors ${res.errors || 0}.`;
    const diagnostics: any = {
      error_code: (res.errors || 0) > 0 ? 'F2ADMIN_RECONCILE_E01' : 'F2ADMIN_RECONCILE_INFO_E01',
      customer: this.commerceRecord._id,
      reconcile_run_id: res.run_id,
      subscriptions_fetched: res.subscriptions_fetched,
      emails_seen: res.emails_seen,
      action_activate: res.activated,
      action_revoke: res.deactivated,
      action_error: res.errors,
    };
    if ((res.errors || 0) > 0) {
      this.toast.error('Reconcile completed with errors', summary, diagnostics);
    } else {
      await this.toast.successWithSupportId('Reconcile complete', summary, diagnostics);
    }
  }

  // ---- SamCart historical snapshots (IT-F2-198 cid a73ce3bf) ----
  //
  // Snapshots are the single source of truth for both download and
  // reconciliation. The old ad-hoc Download popup used ?page=N
  // pagination which f2-session's PR#324 proved to be a 100-sub trap
  // (SamCart ignores page numbers; cursor pagination via
  // pagination.next is the only correct sweep). All download UX now
  // routes through the snapshot picker.

  // Loaded when the Payments & E-Commerce dialog opens on a customer
  // with a SamCart token. Idempotent per slug — cached by
  // samcartSnapshotsLoadedForSlug so re-opening the dialog on the same
  // customer doesn't re-fetch.
  async loadSamcartSnapshots(integ: string, opts?: { force?: boolean }) {
    if (integ !== 'samcart') return;
    if (!this.commerceRecord?._id) return;
    const slug = this.commerceRecord._id;
    if (!opts?.force && this.samcartSnapshotsLoadedForSlug === slug) return;
    this.samcartSnapshotsLoading = true;
    const res = await this.svc.listSamcartSnapshots(slug);
    this.samcartSnapshotsLoading = false;
    if (!res.ok) {
      this.toast.error('Load snapshots failed', res.err || 'unknown error');
      return;
    }
    this.samcartSnapshots = Array.isArray(res.snapshots) ? res.snapshots as any : [];
    // Server returns ascending by started_at; UI wants newest-first.
    this.samcartSnapshots.sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''));
    this.samcartSnapshotsLoadedForSlug = slug;
  }

  // Kicks off a fresh historical sync. Backend is fire-and-forget as of
  // f2-admin-service PR#325: POST returns {ok:true, run_id, status:"pending"}
  // within 100ms; sweep runs on setImmediate. We insert a pending row at
  // top-of-list immediately so the operator sees the sync land, then poll
  // GET /status every 3s (back off to 10s if counts stall across two polls
  // — probable 429 backoff) until status transitions to complete or failed.
  async triggerSamcartSync(integ: string) {
    if (integ !== 'samcart') return;
    if (!this.commerceRecord?._id) return;
    const ui = this.commerceUi[integ];
    if (!ui?.consumer_key) {
      this.toast.error('Missing credentials', 'Save the SamCart API token first.');
      return;
    }
    if (this.samcartActiveSyncRunId) {
      this.toast.error('Sync already running', 'Wait for the current snapshot to complete before starting another.');
      return;
    }
    const slug = this.commerceRecord._id;
    const res = await this.svc.triggerSamcartSyncHistorical(slug);
    if (!res.ok || !res.run_id) {
      this.toast.error('Historical sync failed', res.err || 'unknown error');
      return;
    }
    // Backend <PR#325 blocked and returned the full counts; still handle
    // that case (very small customers may land synchronously).
    if (res.status === 'complete' || res.status === 'failed') {
      const addedCount = Array.isArray(res['new_product_ids_added']) ? res['new_product_ids_added'].length : 0;
      this.toast.success(
        'Historical sync complete',
        `${res['purchases_total'] || 0} purchases • ${res['subs_total'] || 0} subs • ${res['orders_total'] || 0} orders`
          + (addedCount ? ` • ${addedCount} new product_map entries` : ''),
      );
      await this.loadSamcartSnapshots(integ, { force: true });
      return;
    }
    // Async path (post-PR#325) — kick off the poller.
    const runId = String(res.run_id);
    this.samcartActiveSyncRunId = runId;
    this.samcartActiveSyncLastProgress = { subs: 0, orders: 0 };
    this.samcartActiveSyncIntervalMs = 3000;
    // Inject a pending row at top-of-list so the operator sees the sync
    // immediately. The real doc will replace it when the poller sees
    // status=complete + we reload.
    this.samcartSnapshots.unshift({
      run_id: runId,
      customer_slug: slug,
      status: 'pending',
      started_at: new Date().toISOString(),
      completed_at: null,
      subs_total: 0,
      orders_total: 0,
      subs_pages: 0,
      orders_pages: 0,
      customer_ids_total: 0,
      emails_resolved: 0,
      purchases_total: 0,
      new_product_ids_added: [],
      errors: [],
    });
    this._samcartSchedulePoll(integ, runId, slug);
  }

  private _samcartSchedulePoll(integ: string, runId: string, slug: string) {
    this._samcartClearPoll();
    this.samcartActiveSyncPollTimer = setTimeout(
      () => this._samcartPollOnce(integ, runId, slug),
      this.samcartActiveSyncIntervalMs,
    );
  }

  private _samcartClearPoll() {
    if (this.samcartActiveSyncPollTimer) {
      clearTimeout(this.samcartActiveSyncPollTimer);
      this.samcartActiveSyncPollTimer = null;
    }
  }

  private async _samcartPollOnce(integ: string, runId: string, slug: string) {
    // Cancellation guards — operator may have closed the dialog or
    // switched customers mid-sync.
    if (this.samcartActiveSyncRunId !== runId) return;
    if (this.commerceRecord?._id !== slug) {
      this._samcartClearPoll();
      this.samcartActiveSyncRunId = null;
      return;
    }
    const st = await this.svc.getSamcartSnapshotStatus(slug, runId);
    if (this.samcartActiveSyncRunId !== runId) return;
    if (!st.ok) {
      // Transient; keep polling at a slower cadence rather than giving up.
      this.samcartActiveSyncIntervalMs = 10000;
      this._samcartSchedulePoll(integ, runId, slug);
      return;
    }
    // Mirror the live counts onto the pending row in the UI.
    const row = this.samcartSnapshots.find((s) => s.run_id === runId);
    if (row) {
      row.subs_total = Number(st.subs_total || 0);
      row.orders_total = Number(st.orders_total || 0);
      row.purchases_total = Number(st.purchases_total || 0);
      if (st.subs_pages !== undefined) row.subs_pages = Number(st.subs_pages);
      if (st.orders_pages !== undefined) row.orders_pages = Number(st.orders_pages);
      if (Array.isArray(st.errors)) row.errors = st.errors;
    }
    // Terminal state → stop polling, reload the authoritative snapshot
    // doc from the server (picks up new_product_ids_added, emails_resolved,
    // etc. the status envelope omits).
    if (st.status === 'complete' || st.status === 'failed') {
      this._samcartClearPoll();
      this.samcartActiveSyncRunId = null;
      await this.loadSamcartSnapshots(integ, { force: true });
      if (st.status === 'complete') {
        this.toast.success(
          'Historical sync complete',
          `${row?.purchases_total || 0} purchases • ${row?.subs_total || 0} subs • ${row?.orders_total || 0} orders`,
        );
      } else {
        this.toast.error('Historical sync failed', (st.errors || []).join('; ') || 'see snapshot row for details');
      }
      return;
    }
    // Backoff heuristic — if subs + orders didn't advance since the last
    // poll, upstream is probably in 429 backoff; stretch to 10s.
    const prev = this.samcartActiveSyncLastProgress;
    const nowSubs = Number(st.subs_total || 0);
    const nowOrders = Number(st.orders_total || 0);
    if (nowSubs === prev.subs && nowOrders === prev.orders) {
      this.samcartActiveSyncIntervalMs = 10000;
    } else {
      this.samcartActiveSyncIntervalMs = 3000;
    }
    this.samcartActiveSyncLastProgress = { subs: nowSubs, orders: nowOrders };
    this._samcartSchedulePoll(integ, runId, slug);
  }

  // Downloads a snapshot as .json. includeRaw=true piggybacks the
  // ?include=raw opt-in which packs raw subs + orders in addition to
  // the normalized purchases[] — useful for operator-level audits.
  async saveSamcartSnapshot(runId: string, includeRaw: boolean) {
    if (!this.commerceRecord?._id) return;
    const slug = this.commerceRecord._id;
    const key = runId;
    this.samcartSnapshotRowBusy[key] = includeRaw ? 'save-raw' : 'save';
    try {
      const res = await this.svc.getSamcartSnapshot(slug, runId, includeRaw);
      if (!res.ok || !res.snapshot) {
        this.toast.error('Snapshot download failed', res.err || 'unknown error');
        return;
      }
      const blob = new Blob([JSON.stringify(res.snapshot, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = includeRaw ? '-raw' : '';
      const stamp = (res.snapshot.started_at || new Date().toISOString()).slice(0, 10);
      a.download = `samcart-snapshot-${slug}-${stamp}-${runId.slice(0, 8)}${suffix}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      const purchases = Array.isArray(res.snapshot.purchases) ? res.snapshot.purchases.length : (res.snapshot.purchases_total || 0);
      this.toast.success('Snapshot saved', `${purchases} purchases${includeRaw ? ' + raw audit payload' : ''}`);
    } finally {
      this.samcartSnapshotRowBusy[key] = null;
    }
  }

  // Human-friendly recency for the Snapshot picker rows. Reuses the
  // same display shape as formatPingRecency to keep the dialog visually
  // consistent.
  samcartSnapshotRecency(iso: string | null | undefined): string {
    if (!iso) return '(in progress)';
    return this.formatPingRecency(iso);
  }

  trackByRunId(_i: number, row: { run_id: string }): string {
    return row.run_id;
  }


  // ---- WC API: download every subscription as JSON ----
  async commerceDownloadWcSubscriptions(integ: string) {
    if (integ !== 'woocommerce') return;
    if (!this.commerceRecord?._id) return;
    const ui = this.commerceUi[integ];
    if (!ui?.api_base_url || !ui?.consumer_key || (!ui.has_consumer_secret_stored && !ui.consumer_secret_field)) {
      this.toast.error('Missing credentials', 'Save the API base URL + consumer key/secret first.');
      return;
    }
    // Prevent parallel double-clicks + drive the button's spinner state.
    if (ui.downloadingSubscriptions) return;
    ui.downloadingSubscriptions = true;
    try {
      // Streaming path — the service reports page-by-page progress into
      // the global loadingService overlay (which sits above the modal
      // popup at z-[100]). Falls back to the non-streaming shape on
      // any failure inside the reader; the service handles both paths.
      const res = await this.svc.fetchWcSubscriptions(this.commerceRecord._id);
      if (!res?.ok || !res.subscriptions) return;
      const payload = {
        customer_slug: this.commerceRecord._id,
        fetched_at: new Date().toISOString(),
        total: res.total,
        pages: res.pages,
        subscriptions: res.subscriptions,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wc-subscriptions-${this.commerceRecord._id}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.toast.success('Downloaded', `${res.total} subscription(s) from WC`);
    } finally {
      ui.downloadingSubscriptions = false;
    }
  }

  // Human-friendly relative for the "last ping" display.
  formatPingRecency(iso: string | undefined): string {
    if (!iso) return 'never';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return Math.floor(diff) + 's ago';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
  }

  /**
   * Full wall-clock timestamp for the "last ping" hover tooltip.
   * Pairs with formatPingRecency — cell body shows "5m ago", hover
   * reveals the exact ET day/date/time + a UTC line. Mike 2026-07-06.
   */
  formatFullPingWhen(iso: string | undefined): string {
    if (!iso) return 'never pinged';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const et = d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true, timeZoneName: 'short',
    });
    const utc = d.toISOString().slice(0, 19).replace('T', ' ') + ' UTC';
    return `${et}\n${utc}`;
  }

  async commerceResetPingStats(integ: string) {
    if (!this.commerceRecord?._id) return;
    const ok = (typeof window !== 'undefined' && (window as any).confirm)
      ? (window as any).confirm(`Reset ping stats for ${integ}? Most-recent ping + today/month/lifetime counts will be cleared.`)
      : true;
    if (!ok) return;
    const res: any = await this.svc.clearCommercePingStats(this.commerceRecord._id, integ);
    if (res?.err) return;
    delete this.commerceMostRecentPing[integ];
    this.commercePingCounts[integ] = { today: 0, month: 0, lifetime: 0 };
  }

  commerceAssetsFor(integrationKey: string): AssetSummary[] {
    const def = this.COMMERCE_INTEGRATIONS.find(i => i.key === integrationKey);
    if (!def) return [];
    return this.commerceAssets.filter(a => a.category === def.asset_category);
  }

  serveUrlForAsset(asset: AssetSummary): string {
    if (!this.commerceRecord?._id) return '#';
    return this.assetsSvc.serveUrl(this.commerceRecord._id, asset.id);
  }

  // Live-rendered, slug-substituted guide URL — preferred surface so the
  // delivery URL example in the guide is always correct for the viewing
  // customer (vs. uploaded static assets that bake the slug literally).
  liveGuideUrlFor(integrationKey: string): string | null {
    if (!this.commerceRecord?._id) return null;
    if (integrationKey === 'woocommerce') {
      return `${env.apiUrl}/rest/admin/customer-assets/${encodeURIComponent(this.commerceRecord._id)}/woocommerce-guide`;
    }
    return null;
  }

  // Scanner catalog for the dropdown — {id, name, disabled} triples
  // for the customer currently in the popup. Loaded live in
  // openCommerceSettings so display names match F2-ADMIN.Scanners and
  // never drift from the canonical source.
  commerceScannerOptions(): Array<{ id: string; name: string; disabled: boolean }> {
    return this.commerceScannerCatalog;
  }

  commerceScannerIdInCatalog(id: string): boolean {
    return !!this.commerceScannerCatalog.find((s) => s.id === id);
  }

  // IT-F2-87 (2026-08-05): per-integration bound provider that the
  // ProductMapEditor invokes on Copy-to-Excel click to fetch fresh
  // {product_id: past_buyer_count} counts. Memoized per integration
  // so template `[pastBuyerCountsProvider]="..."` bindings return a
  // stable reference (avoids re-instantiating the arrow every CD tick,
  // which would trigger the editor's OnPush inputs to re-fire).
  private _pastBuyerCountsProviderCache = new Map<string, () => Promise<Record<string, number>>>();
  commercePastBuyerCountsProvider(integration: string): () => Promise<Record<string, number>> {
    const slug = this.commerceRecord?._id || '';
    const cacheKey = `${slug}::${integration}`;
    let fn = this._pastBuyerCountsProviderCache.get(cacheKey);
    if (!fn) {
      fn = () => (slug ? this.svc.getPastBuyerCounts(slug, integration) : Promise.resolve({}));
      this._pastBuyerCountsProviderCache.set(cacheKey, fn);
    }
    return fn;
  }

  // ----- secret field handlers -----

  commerceCanGenerate(integ: string): boolean {
    const ui = this.commerceUi[integ];
    if (!ui) return false;
    // Generate is only available when nothing is stored AND nothing is
    // in the field. Mike's rule: must be empty before generate.
    return !ui.has_secret_stored && !ui.secret_field;
  }

  async commerceToggleReveal(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui || !this.commerceRecord?._id) return;
    if (ui.secret_revealed) {
      ui.secret_revealed = false;
      return;
    }
    if (ui.secret_field) {
      // local value already present (typed / pasted / freshly generated)
      ui.secret_revealed = true;
      return;
    }
    if (!ui.has_secret_stored) {
      // nothing to reveal
      ui.secret_revealed = true;
      return;
    }
    const res: any = await this.svc.revealCommerceSecret(this.commerceRecord._id, integ);
    if (res?.err) return;
    ui.secret_field = String(res.secret || '');
    ui.secret_revealed = true;
  }

  /**
   * Build the absolute delivery URL an upstream storefront (WooCommerce,
   * Kartra, SamCart, ...) POSTs its webhooks to. All three ready
   * integrations mount at `/rest/{integ.key}/{customer_slug}/subscription-event`
   * on f2-admin-service2, so the URL is derivable from just the
   * integration key and the currently-open customer slug.
   *
   * Same-TLD by host — admin.f2-tech.ai talks to
   * f2-admin-service2.f2-tech.ai, .com to .com. Preserves the "never
   * cross TLD" hard rule that env.apiUrl also honors.
   */
  commerceDeliveryUrl(integKey: string): string {
    const slug = this.commerceRecord?._id;
    if (!slug) return '';
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const tld = host.endsWith('.ai') ? 'ai' : 'com';
    return `https://f2-admin-service2.f2-tech.${tld}/rest/${integKey}/${encodeURIComponent(slug)}/subscription-event`;
  }

  /**
   * Copy the auto-generated delivery URL to the clipboard. Independent of
   * webhook_secret storage — the URL is always derivable (customer slug
   * lives on the open editor row), so this works even before the
   * webhook_secret has been saved.
   */
  async commerceCopyDeliveryUrl(integKey: string) {
    const url = this.commerceDeliveryUrl(integKey);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.toast.success('Copied', 'Delivery URL copied to clipboard');
    } catch (e: any) {
      this.toast.error('Copy failed', e?.message || 'Clipboard write blocked');
    }
  }

  /**
   * Generic clipboard-copy for small text values (webhook topic strings,
   * etc.). Toast body describes what was copied so the operator sees the
   * confirmation without opening the clipboard.
   */
  async commerceCopyText(text: string, label: string = 'Value') {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      this.toast.success('Copied', `${label} copied to clipboard`);
    } catch (e: any) {
      this.toast.error('Copy failed', e?.message || 'Clipboard write blocked');
    }
  }

  async commerceCopy(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui || !this.commerceRecord?._id) return;
    let value = ui.secret_field;
    if (!value && ui.has_secret_stored) {
      const res: any = await this.svc.revealCommerceSecret(this.commerceRecord._id, integ);
      if (res?.err) return;
      value = String(res.secret || '');
      ui.secret_field = value;
      ui.secret_revealed = true;
    }
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      this.toast.success('Copied', 'Webhook secret copied to clipboard');
    } catch (e: any) {
      this.toast.error('Copy failed', e?.message || 'Clipboard write blocked');
    }
  }

  async commercePaste(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui) return;
    try {
      const text = await navigator.clipboard.readText();
      ui.secret_field = (text || '').trim();
      ui.secret_revealed = true;
      ui.secret_dirty = true;
    } catch (e: any) {
      this.toast.error('Paste failed', e?.message || 'Clipboard read blocked');
    }
  }

  async commerceGenerate(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui || !this.commerceRecord?._id) return;
    if (!this.commerceCanGenerate(integ)) {
      this.toast.error('Cannot generate', 'Clear the secret field first');
      return;
    }
    const res: any = await this.svc.setCommerceSecret(
      this.commerceRecord._id, integ, { generate: true },
    );
    if (res?.err) return;
    ui.secret_field = String(res.secret || '');
    ui.secret_revealed = true;
    ui.has_secret_stored = true;
    ui.secret_dirty = false;
    // Generate auto-enables — the customer doc gets `enabled: true` as a
    // side effect on the server, mirror it here so the UI doesn't disagree.
    ui.enabled = true;
  }

  async commerceClear(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui || !this.commerceRecord?._id) return;
    if (ui.has_secret_stored) {
      const res: any = await this.svc.clearCommerceSecret(this.commerceRecord._id, integ);
      if (res?.err) return;
    }
    ui.secret_field = '';
    ui.secret_revealed = false;
    ui.has_secret_stored = false;
    ui.secret_dirty = false;
  }

  async commerceSave(integ: string) {
    const ui = this.commerceUi[integ];
    if (!ui || !this.commerceRecord?._id) return;
    // Push secret first if dirty (paste/typing). Skip when not dirty —
    // generate already persisted server-side.
    if (ui.secret_dirty && ui.secret_field) {
      const sres: any = await this.svc.setCommerceSecret(
        this.commerceRecord._id, integ, { plaintext: ui.secret_field },
      );
      if (sres?.err) return;
      ui.has_secret_stored = true;
      ui.secret_dirty = false;
    }
    const product_map = this._serializeProductMap(integ);
    const body: any = {
      enabled: ui.enabled,
      default_scanner: ui.default_scanner || null,
      ping_log: ui.ping_log,
      api_base_url: ui.api_base_url || '',
      consumer_key: ui.consumer_key || '',
      product_map,
    };
    // Only include consumer_secret in the body when the user actually
    // typed/pasted a new value; otherwise we'd echo back the stored
    // plaintext on every save round-trip.
    //
    // IT-F2-378 cid 017e04ba — Keap's second credential is a REST PAT
    // written to keap.pat (confirmed by f2-session c/bb3e189c; backend
    // reads only cfg.pat, no fallback to consumer_secret). Route the
    // dirty consumer_secret_field to body.pat for Keap and SKIP the
    // generic consumer_secret write so we don't persist the PAT under
    // two keys.
    if (ui.consumer_secret_dirty && ui.consumer_secret_field) {
      if (integ === 'keap') {
        body.pat = ui.consumer_secret_field;
      } else {
        body.consumer_secret = ui.consumer_secret_field;
      }
    }
    // Provider-specific API credentials (currently just Kartra — 3
    // fields: app_id / api_key / api_password). Only include fields
    // that are dirty AND non-empty, matching the consumer_secret pattern
    // — omission means "leave alone" server-side. api_credentials nests
    // in the body so the backend routes each field to the correct
    // <integration>.api_credentials.<field> dot-path.
    if (ui.api_credentials) {
      const ac = ui.api_credentials;
      const acBody: Record<string, string> = {};
      // Bracket notation on the Record<string,string> writes to satisfy
      // TS4111 (noPropertyAccessFromIndexSignature).
      if (ac.app_id_dirty && ac.app_id) acBody['app_id'] = ac.app_id;
      if (ac.api_key_dirty && ac.api_key) acBody['api_key'] = ac.api_key;
      if (ac.api_password_dirty && ac.api_password_field) acBody['api_password'] = ac.api_password_field;
      if (Object.keys(acBody).length) body.api_credentials = acBody;
    }
    const res: any = await this.svc.updateCommerce(this.commerceRecord._id, integ, body);
    if (res?.err) return;
    // IT-F2-104 v2 Option A (2026-08-03): backend auto-fires the remap sweep
    // on any product_map entry that transitions unassigned→real or gains
    // grants[]. Surface the aggregate result as a toast so the operator sees
    // what landed on past buyers as a side-effect of Save (no manual button).
    if (Array.isArray(res?.remap_summaries) && res.remap_summaries.length) {
      const rs = res.remap_summaries as Array<any>;
      const totals = rs.reduce(
        (acc, s) => {
          acc.products += 1;
          acc.emails += Number(s?.affected_emails || 0);
          acc.activated += Number(s?.activated || 0);
          acc.already += Number(s?.already || 0);
          acc.errors += Number(s?.errors || 0);
          return acc;
        },
        { products: 0, emails: 0, activated: 0, already: 0, errors: 0 },
      );
      const parts: string[] = [];
      parts.push(`${totals.products} product${totals.products === 1 ? '' : 's'}, ${totals.emails} past buyer${totals.emails === 1 ? '' : 's'} swept`);
      if (totals.activated) parts.push(`${totals.activated} grant${totals.activated === 1 ? '' : 's'} activated`);
      if (totals.already) parts.push(`${totals.already} already had access`);
      if (totals.errors) parts.push(`${totals.errors} error${totals.errors === 1 ? '' : 's'}`);
      const msg = parts.join(' · ');
      if (totals.errors > 0) this.toast.error('Remap swept with errors', msg);
      else this.toast.success('Retro-remap applied', msg);
    }
    // Successful save -- update baseline so dirty flag resets and the
    // Close button drops the "lose changes" warning.
    this.commerceUiBaseline[integ] = {
      enabled: ui.enabled,
      default_scanner: ui.default_scanner,
      ping_log: ui.ping_log,
      api_base_url: ui.api_base_url || '',
      consumer_key: ui.consumer_key || '',
      product_map: { ...product_map },
    };
    // IT-F2-432 c/ac99194c — reset the explicit dirty flag on save so the
    // "unsaved" indicator next to the API Token input drops away.
    ui.consumer_key_dirty = false;
    if (body.consumer_secret) {
      ui.has_consumer_secret_stored = true;
      ui.consumer_secret_dirty = false;
    }
    // Reset kartra api_credentials dirty flags on successful save;
    // has_api_password_stored flips true when api_password was written.
    if (ui.api_credentials && body.api_credentials) {
      const ac = ui.api_credentials;
      if (body.api_credentials['app_id']) ac.app_id_dirty = false;
      if (body.api_credentials['api_key']) ac.api_key_dirty = false;
      if (body.api_credentials['api_password']) {
        ac.has_api_password_stored = true;
        ac.api_password_dirty = false;
        ac.api_password_field = '';   // clear the input so it renders as ••• on next open
      }
    }
  }

  // <input> bound to ui.secret_field; track that user typed → dirty.
  // (input) is the obvious binding but browser password autofill /
  // 1Password / LastPass all fire (input) without user interaction,
  // which falsely marks the field dirty the instant the popup opens.
  // (keydown), (paste), and (cut) only fire on real user activity --
  // autofill stamps the value programmatically without triggering
  // any of these. Modifier-only keypresses (Ctrl alone, Shift alone,
  // arrow keys, etc.) are filtered so the secret_dirty flag only flips
  // when an actual editing action is taken.
  onSecretUserInput(integ: string, ev?: KeyboardEvent) {
    const ui = this.commerceUi[integ];
    if (!ui) return;
    if (ev && 'key' in ev) {
      const k = String(ev.key || '');
      // Ignore modifier-only / navigation keys -- those don't change
      // the field's value so they shouldn't mark it dirty.
      const noop = new Set([
        'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab', 'Escape',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'PageUp', 'PageDown',
      ]);
      if (noop.has(k)) return;
      // Ctrl/Cmd+C (copy) shouldn't mark dirty either.
      if ((ev.ctrlKey || ev.metaKey) && k.toLowerCase() === 'c') return;
    }
    ui.secret_dirty = true;
  }

  // Compatibility shim — older templates may still call this name.
  commerceSecretTyped(integ: string) {
    this.onSecretUserInput(integ);
  }

  // ===== Email Provider (Customer.io) handlers =====
  // One config per customer, lives at F2-ADMIN.Customers.<slug>.email_provider.
  // The popup row is a sibling of the integration cards; api_key follows
  // the same reveal/copy/paste/clear pattern as the WC webhook secret.

  // Parse the CSV bridge field back to a list. Trim + drop empties.
  private _parseTriggerCsv(s: string): string[] {
    return String(s || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }

  commerceEmailIsDirty(): boolean {
    const ui = this.commerceEmailUi;
    const base = this.commerceEmailBaseline;
    if (!ui || !base) return false;
    if (ui.enabled !== base.enabled) return true;
    if (ui.kind !== base.kind) return true;
    if (ui.site_id !== base.site_id) return true;
    if (ui.segment_id !== base.segment_id) return true;
    if (ui.attribute_name !== base.attribute_name) return true;
    if (ui.send_via !== base.send_via) return true;
    if (ui.triggers_text !== base.triggers_text) return true;
    if (ui.api_key_dirty) return true;
    return false;
  }

  async commerceEmailToggleReveal() {
    const ui = this.commerceEmailUi;
    if (!ui || !this.commerceRecord?._id) return;
    if (ui.api_key_revealed) {
      ui.api_key_revealed = false;
      return;
    }
    if (ui.api_key_field) {
      ui.api_key_revealed = true;
      return;
    }
    if (!ui.has_api_key_stored) {
      ui.api_key_revealed = true;
      return;
    }
    const res: any = await this.svc.revealEmailProviderApiKey(this.commerceRecord._id);
    if (res?.err) return;
    ui.api_key_field = String(res.api_key || '');
    ui.api_key_revealed = true;
  }

  async commerceEmailCopyApiKey() {
    const ui = this.commerceEmailUi;
    if (!ui || !this.commerceRecord?._id) return;
    let value = ui.api_key_field;
    if (!value && ui.has_api_key_stored) {
      const res: any = await this.svc.revealEmailProviderApiKey(this.commerceRecord._id);
      if (res?.err) return;
      value = String(res.api_key || '');
      ui.api_key_field = value;
      ui.api_key_revealed = true;
    }
    if (!value) return;
    this.copyToClipboard(value);
  }

  async commerceEmailPasteApiKey() {
    const ui = this.commerceEmailUi;
    if (!ui) return;
    try {
      const text = await navigator.clipboard.readText();
      ui.api_key_field = (text || '').trim();
      ui.api_key_revealed = true;
      ui.api_key_dirty = true;
    } catch (e: any) {
      this.toast.error('Paste failed', e?.message || 'Clipboard read blocked');
    }
  }

  async commerceEmailClearApiKey() {
    const ui = this.commerceEmailUi;
    if (!ui || !this.commerceRecord?._id) return;
    if (ui.has_api_key_stored) {
      const res: any = await this.svc.clearEmailProviderApiKey(this.commerceRecord._id);
      if (res?.err) return;
    }
    ui.api_key_field = '';
    ui.api_key_revealed = false;
    ui.has_api_key_stored = false;
    ui.api_key_dirty = false;
  }

  // Same keydown/paste/cut pattern used on the WC webhook secret to
  // beat browser/password-manager autofill marking the field dirty
  // on popup open.
  onEmailApiKeyUserInput(ev?: KeyboardEvent) {
    const ui = this.commerceEmailUi;
    if (!ui) return;
    if (ev && 'key' in ev) {
      const k = String(ev.key || '');
      const noop = new Set([
        'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab', 'Escape',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'PageUp', 'PageDown',
      ]);
      if (noop.has(k)) return;
      if ((ev.ctrlKey || ev.metaKey) && k.toLowerCase() === 'c') return;
    }
    ui.api_key_dirty = true;
  }

  commerceEmailRevert() {
    const ui = this.commerceEmailUi;
    const base = this.commerceEmailBaseline;
    if (!ui || !base) return;
    if (!this.commerceEmailIsDirty()) return;
    const ok = (typeof window !== 'undefined' && (window as any).confirm)
      ? (window as any).confirm('Discard unsaved email provider changes?')
      : true;
    if (!ok) return;
    ui.enabled = base.enabled;
    ui.kind = base.kind;
    ui.site_id = base.site_id;
    ui.segment_id = base.segment_id;
    ui.attribute_name = base.attribute_name;
    ui.send_via = base.send_via;
    ui.triggers_text = base.triggers_text;
    ui.api_key_field = '';
    ui.api_key_revealed = false;
    ui.api_key_dirty = false;
  }

  async commerceEmailSave() {
    const ui = this.commerceEmailUi;
    if (!ui || !this.commerceRecord?._id) return;
    const body: any = {
      enabled: ui.enabled,
      kind: ui.kind || 'customer_io',
      site_id: ui.site_id || '',
      segment_id: ui.segment_id || '',
      attribute_name: ui.attribute_name || '',
      send_via: ui.send_via || 'customer_io',
      triggers_on_scanners: this._parseTriggerCsv(ui.triggers_text),
    };
    // Only push api_key when the operator actually pasted/typed a new
    // value; otherwise the round-trip would echo back a stored value
    // we never had on the client.
    if (ui.api_key_dirty && ui.api_key_field) {
      body.api_key = ui.api_key_field;
    }
    const res: any = await this.svc.updateEmailProvider(this.commerceRecord._id, body);
    if (res?.err) return;
    if (body.api_key) {
      ui.has_api_key_stored = true;
      ui.api_key_dirty = false;
    }
    // Refresh baseline so dirty resets + the "lose changes" gate clears.
    this.commerceEmailBaseline = {
      enabled: ui.enabled,
      kind: ui.kind || 'customer_io',
      send_via: ui.send_via || 'customer_io',
      site_id: ui.site_id || '',
      segment_id: ui.segment_id || '',
      attribute_name: ui.attribute_name || '',
      triggers_text: body.triggers_on_scanners.join(', '),
    };
    ui.triggers_text = this.commerceEmailBaseline.triggers_text;
  }
  // Enable / disable the whole portal handoff via the big top-of-popup toggle.
  psToggleEnabled() {
    if (!this.portalSettingsRecord) return;
    if (this.portalSettingsRecord.sso_portal) {
      this.portalSettingsRecord.sso_portal = undefined;
      this.psRequiredClaimsText = '';
      this.psAllowedMembershipsText = '';
      this.psMembershipMapJson = '{}';
      this.psClaimTemplateJson = '{}';
    } else {
      this.applyPreset('jwtsso');
    }
  }
  // One-click presets — fill the form with sensible defaults for common shapes.
  // 'jwtsso'    = Simpler-style portal: email + name + memberships + perms + type
  // 'legacy'    = TheoTrade/MTA-style: separated names, scanner via JWT, no perms
  // 'theov2'    = TheoTrade v2 minimal: base64 email/phone, separated names,
  //               no scanner (members-page fallback), allow_create_account per-JWT
  applyPreset(kind: 'jwtsso' | 'legacy' | 'theov2') {
    if (!this.portalSettingsRecord) return;
    if (kind === 'jwtsso') {
      this.portalSettingsRecord.sso_portal = {
        enabled: true,
        expected_type: 'JWTSSO',
        required_claims: ['email', 'name', 'memberships', 'perms', 'type', 'iat', 'exp'],
        allowed_memberships: [],
        scanner_id: '',
        scanner_selection: { mode: 'fixed' },
        identity_mapping: {},
        embed_modes: ['redirect'],
        sid_ttl_seconds: 90,
        docs_url: '',
        claim_template: { scanner: '{scanner_id}', live_data: 'true' },
      };
      this.psRequiredClaimsText = 'email, name, memberships, perms, type, iat, exp';
      this.psAllowedMembershipsText = '';
      this.psMembershipMapJson = '{}';
      this.psClaimTemplateJson = '{\n  "scanner": "{scanner_id}",\n  "live_data": "true"\n}';
    } else if (kind === 'legacy') {
      // Legacy / customer-shape: TheoTrade-style separated fields,
      // scanner via from_jwt using Scanners.SsoAliases, no memberships,
      // no type discriminator.
      this.portalSettingsRecord.sso_portal = {
        enabled: true,
        expected_type: '',
        required_claims: ['email', 'first_name', 'last_name', 'scanner', 'scanners', 'live_data', 'nbf', 'exp'],
        allowed_memberships: [],
        scanner_id: '',
        scanner_selection: { mode: 'from_jwt', scanner_claim: 'scanner' },
        identity_mapping: { email: 'email', first_name: 'first_name', last_name: 'last_name' },
        embed_modes: ['redirect', 'iframe'],
        sid_ttl_seconds: 90,
        docs_url: '',
        claim_template: { scanner: '{scanner_id}', live_data: '{payload.live_data}' },
      };
      this.psRequiredClaimsText = 'email, first_name, last_name, scanner, scanners, live_data, nbf, exp';
      this.psAllowedMembershipsText = '';
      this.psMembershipMapJson = '{}';
      this.psClaimTemplateJson = '{\n  "scanner": "{scanner_id}",\n  "live_data": "{payload.live_data}"\n}';
      this.psOptionalClaimsText = '';
      this.psBase64FieldsText = '';
    } else {
      // TheoTrade v2 — minimal payload with base64-encoded email + phone,
      // optional phone, no scanner in JWT (members-page fallback),
      // allow_create_account per-JWT control. PDF v2 contract.
      this.portalSettingsRecord.sso_portal = {
        enabled: true,
        expected_type: '',
        required_claims: ['email', 'first_name', 'last_name', 'iat'],
        optional_claims: ['phone', 'allow_create_account'],
        allowed_memberships: [],
        scanner_id: '',                                       // empty → members-page fallback
        scanner_selection: { mode: 'fixed' },
        identity_mapping: {
          email: 'email',
          first_name: 'first_name',
          last_name: 'last_name',
          phone: 'phone',
          base64_fields: ['email', 'phone'],
        },
        allow_create_account_enabled: true,
        max_iat_age_seconds: 60,
        embed_modes: ['redirect', 'iframe'],
        sid_ttl_seconds: 90,
        docs_url: '/docs/sso-integration-guide-v2.md',
        claim_template: undefined,
      };
      this.psRequiredClaimsText = 'email, first_name, last_name, iat';
      this.psAllowedMembershipsText = '';
      this.psMembershipMapJson = '{}';
      this.psClaimTemplateJson = '{}';
      this.psOptionalClaimsText = 'phone, allow_create_account';
      this.psBase64FieldsText = 'email, phone';
    }
  }
  // Toggle helpers reused from main edit modal (allowed_scanners + embed_modes).
  psIsScannerAllowed(scannerId: string): boolean {
    return !!this.portalSettingsRecord?.sso_portal?.allowed_scanners?.includes(scannerId);
  }
  psToggleScannerAllowed(scannerId: string) {
    const sp = this.portalSettingsRecord?.sso_portal;
    if (!sp) return;
    const cur = sp.allowed_scanners || [];
    sp.allowed_scanners = cur.includes(scannerId) ? cur.filter(s => s !== scannerId) : [...cur, scannerId];
  }
  psOwnedScannerIds(): string[] {
    return this.portalSettingsRecord?.scanner_ids || [];
  }
  // default_scanners chip management — concrete IDs from the dropdown
  // OR a wildcard pattern (e.g. 'tt-*') typed in the freeform input.
  // Operator can mix the two; duplicates are silently de-duped.
  psAddDefaultScanner(value: string) {
    const v = String(value || '').trim();
    if (!v) return;
    if (this.psDefaultScannersList.includes(v)) return;
    this.psDefaultScannersList = [...this.psDefaultScannersList, v];
    this.psDefaultScannersInput = '';
  }
  psRemoveDefaultScanner(value: string) {
    this.psDefaultScannersList = this.psDefaultScannersList.filter(v => v !== value);
  }
  // Owned scanners NOT yet in default_scanners — drives the "add from
  // catalog" dropdown options so the operator can't double-pick a
  // scanner that's already chipped.
  psAvailableScannerOptions(): string[] {
    return this.psOwnedScannerIds().filter(sid => !this.psDefaultScannersList.includes(sid));
  }
  psIsEmbedMode(mode: 'iframe' | 'redirect'): boolean {
    return !!this.portalSettingsRecord?.sso_portal?.embed_modes?.includes(mode);
  }
  psToggleEmbedMode(mode: 'iframe' | 'redirect') {
    const sp = this.portalSettingsRecord?.sso_portal;
    if (!sp) return;
    const cur = sp.embed_modes || [];
    sp.embed_modes = cur.includes(mode) ? cur.filter(m => m !== mode) : [...cur, mode];
  }
  // Save via the same update endpoint the main edit modal uses. We send
  // ONLY the sso_portal field (plus _id implicitly via the URL) so other
  // unrelated Customer fields are unaffected.
  async psSave() {
    if (!this.portalSettingsRecord?._id) return;
    const sp = this.portalSettingsRecord.sso_portal;
    let merged: SsoPortal | undefined = undefined;
    if (sp) {
      let membership_map: Record<string, string> = {};
      let claim_template: Record<string, string> = {};
      try { membership_map = JSON.parse(this.psMembershipMapJson || '{}'); }
      catch (e: any) { this.toast.error('Invalid membership_map JSON', e.message); return; }
      try { claim_template = JSON.parse(this.psClaimTemplateJson || '{}'); }
      catch (e: any) { this.toast.error('Invalid claim_template JSON', e.message); return; }
      const optionalClaims = this.psOptionalClaimsText.split(',').map(s => s.trim()).filter(Boolean);
      const base64Fields = this.psBase64FieldsText.split(',').map(s => s.trim()).filter(Boolean);
      const defaultScanners = this.psDefaultScannersList.map(s => String(s || '').trim()).filter(Boolean);
      merged = {
        ...sp,
        required_claims: this.psRequiredClaimsText.split(',').map(s => s.trim()).filter(Boolean),
        allowed_memberships: this.psAllowedMembershipsText.split(',').map(s => s.trim()).filter(Boolean),
        scanner_selection: sp.scanner_selection ? {
          mode: sp.scanner_selection.mode,
          scanner_claim: sp.scanner_selection.scanner_claim || undefined,
          membership_map: sp.scanner_selection.mode === 'from_membership' && Object.keys(membership_map).length > 0 ? membership_map : undefined,
        } : undefined,
        claim_template: Object.keys(claim_template).length > 0 ? claim_template : undefined,
        optional_claims: optionalClaims.length > 0 ? optionalClaims : undefined,
        default_scanners: defaultScanners.length > 0 ? defaultScanners : undefined,
        support_contact_html: this.psSupportContactHtml.trim() ? this.psSupportContactHtml : undefined,
        identity_mapping: sp.identity_mapping ? {
          ...sp.identity_mapping,
          base64_fields: base64Fields.length > 0 ? base64Fields : undefined,
        } : (base64Fields.length > 0 ? { base64_fields: base64Fields } : undefined),
      };
    }
    // Same null-as-unset signal as saveModal -- so disabling the portal
    // from the focused popup actually removes sso_portal from the doc.
    // mta_endpoint_allowed rides alongside sso_portal in the same partial
    // update so a single Save button covers both toggles. `false` and
    // `null` both semantically mean "not allowed" — send `null` so the
    // field is $unset from the doc rather than persisted as a literal
    // false (keeps docs clean: absence means "not allowed" by default).
    // MTA is implicitly allowed on its own endpoint and cannot be
    // toggled off — skip writing the field for the mta customer at all.
    const partial: any = { sso_portal: merged ?? null };
    if (this.portalSettingsRecord._id !== 'mta') {
      partial.mta_endpoint_allowed = this.portalSettingsRecord.mta_endpoint_allowed ? true : null;
    }
    const res = await this.svc.update(this.portalSettingsRecord._id, partial);
    if (res?.err) return;
    this.closePortalSettings();
    await this.refresh();
  }
  // Quick deep-link into the SSO Tester pre-selecting this customer.
  // Just navigates — the Tester reads the customer list itself.
  psOpenInTester() {
    if (!this.portalSettingsRecord?._id) return;
    window.open('/admin/sso-tester', '_blank');
  }
  // Secret management from inside the popup. Reuses the existing list /
  // generate / delete service methods but scoped to portalSettingsRecord
  // instead of editing.
  async psGenerateSecret() {
    if (!this.portalSettingsRecord?._id) return;
    const res = await this.svc.generateSsoSecret(this.portalSettingsRecord._id, this.revealSecretLabel || undefined);
    if ((res as any)?.err) return;
    this.revealedSecret = res as any;
    this.revealSecretLabel = '';
    this.ssoSecrets = await this.svc.listSsoSecrets(this.portalSettingsRecord._id);
  }
  // Import an operator-supplied plaintext secret (e.g. one the customer's
  // portal already signs with). Same envelope as generate; the label tags
  // it for ops. Backend dupes on identical fingerprint to avoid silent
  // double-adds. Clears the textarea + label on success.
  async psImportSecret() {
    if (!this.portalSettingsRecord?._id) return;
    const pt = this.importSecretPlaintext.trim();
    if (!pt) return;
    const res = await this.svc.importSsoSecret(this.portalSettingsRecord._id, pt, this.importSecretLabel.trim() || undefined);
    if ((res as any)?.err) return;
    this.importSecretPlaintext = '';
    this.importSecretLabel = '';
    this.ssoSecrets = await this.svc.listSsoSecrets(this.portalSettingsRecord._id);
  }
  async psDeleteSecret(id: string) {
    if (!this.portalSettingsRecord?._id) return;
    if (!confirm('Delete this secret? Any JWTs signed by it will stop validating.')) return;
    const res = await this.svc.deleteSsoSecret(this.portalSettingsRecord._id, id);
    if (res?.err) return;
    this.ssoSecrets = await this.svc.listSsoSecrets(this.portalSettingsRecord._id);
  }

  // ----- perms_schema row management -----
  addPermsField() {
    if (!this.editing.sso_portal) return;
    if (!this.editing.sso_portal.perms_schema) this.editing.sso_portal.perms_schema = [];
    this.editing.sso_portal.perms_schema.push({ key: '', label: '', type: 'string' });
  }
  removePermsField(idx: number) {
    if (!this.editing.sso_portal?.perms_schema) return;
    this.editing.sso_portal.perms_schema.splice(idx, 1);
  }
  setPermsFieldOptions(field: PermsSchemaField, value: string) {
    field.options = value.split(',').map(s => s.trim()).filter(Boolean);
  }
  permsFieldOptionsText(field: PermsSchemaField): string {
    return (field.options || []).join(', ');
  }

  // Toggle one embed mode on/off. Iframe + redirect are independent — a
  // customer can support either or both.
  toggleEmbedMode(mode: 'iframe' | 'redirect') {
    if (!this.editing.sso_portal) return;
    const cur = this.editing.sso_portal.embed_modes || [];
    if (cur.includes(mode)) {
      this.editing.sso_portal.embed_modes = cur.filter(m => m !== mode);
    } else {
      this.editing.sso_portal.embed_modes = [...cur, mode];
    }
  }

  isEmbedMode(mode: 'iframe' | 'redirect'): boolean {
    return !!this.editing.sso_portal?.embed_modes?.includes(mode);
  }

  // ----- Shared-secret management -----

  private async _loadSsoSecrets(slug: string) {
    this.ssoSecretsLoading = true;
    try {
      this.ssoSecrets = await this.svc.listSsoSecrets(slug);
    } finally {
      this.ssoSecretsLoading = false;
    }
  }

  async generateSsoSecret() {
    if (!this.editing._id || this.isCreate) {
      this.toast.error('Save first', 'Create the customer before generating a secret.');
      return;
    }
    const res = await this.svc.generateSsoSecret(this.editing._id, this.revealSecretLabel || undefined);
    if ((res as any)?.err) return;
    this.revealedSecret = res as any;
    this.revealSecretLabel = '';
    await this._loadSsoSecrets(this.editing._id);
  }

  async deleteSsoSecret(id: string) {
    if (!this.editing._id) return;
    if (!confirm('Delete this secret? Any JWTs signed by it will stop validating.')) return;
    const res = await this.svc.deleteSsoSecret(this.editing._id, id);
    if (res?.err) return;
    await this._loadSsoSecrets(this.editing._id);
  }

  dismissReveal() {
    this.revealedSecret = null;
  }

  copyRevealedSecret() {
    if (!this.revealedSecret) return;
    navigator.clipboard.writeText(this.revealedSecret.secret);
    this.toast.success('Copied', 'Secret copied to clipboard');
  }

  // Add the current `domainInput` value as a chip. Lowercases + trims;
  // skips duplicates. Called on Enter, blur, or paste-with-comma.
  addDomain() {
    const raw = (this.domainInput || '').trim().toLowerCase();
    if (!raw) return;
    // Split on comma too, so paste-list "a.com, b.com" works.
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (!this.editing.domains) this.editing.domains = [];
    for (const p of parts) {
      if (!this.editing.domains.includes(p)) this.editing.domains.push(p);
    }
    this.domainInput = '';
  }

  removeDomain(d: string) {
    if (!this.editing.domains) return;
    this.editing.domains = this.editing.domains.filter((x) => x !== d);
  }

  // Backspace on empty input pops the last chip — standard chip-editor UX.
  // Signature is Event (not KeyboardEvent) because Angular's strict template
  // type-checker passes `$event` as the generic Event type for keydown.* bindings.
  onDomainBackspace(ev: Event) {
    if (this.domainInput) return;
    const list = this.editing.domains || [];
    if (!list.length) return;
    ev.preventDefault();
    list.pop();
    this.editing.domains = [...list];
  }

  closeModal() {
    this.showModal = false;
  }

  async saveModal() {
    // Flush any pending text in the domain chip input before serializing.
    if (this.domainInput) this.addDomain();
    // Coerce CSV-style inputs into arrays
    const repos = this.reposText.split(',').map((s) => s.trim()).filter(Boolean);
    const vercel = this.vercelText.split(',').map((s) => s.trim()).filter(Boolean);
    const scanners = this.scannerIdsText.split(',').map((s) => s.trim()).filter(Boolean);
    // Backend canonicalises (lowercase + trim + dedupe); UI keeps the chip list.
    const domains = this.editing.domains || [];

    // Parse the JSON-ish fields (urls, contacts) — if invalid, show toast
    let urls: any[] = [];
    let contacts: any[] = [];
    try { urls = JSON.parse(this.urlsJsonText || '[]'); } catch (e: any) {
      this.toast.error('Invalid URLs JSON', e.message);
      return;
    }
    try { contacts = JSON.parse(this.contactsJsonText || '[]'); } catch (e: any) {
      this.toast.error('Invalid contacts JSON', e.message);
      return;
    }

    // Coerce sso_portal CSV / JSON bridges back to typed values when the
    // section is on. JSON parse errors abort the save — same UX as urls/contacts.
    let sso_portal: SsoPortal | undefined = undefined;
    if (this.editing.sso_portal) {
      let membership_map: Record<string, string> = {};
      let claim_template: Record<string, string> = {};
      try { membership_map = JSON.parse(this.ssoPortalMembershipMapJson || '{}'); }
      catch (e: any) { this.toast.error('Invalid membership_map JSON', e.message); return; }
      try { claim_template = JSON.parse(this.ssoPortalClaimTemplateJson || '{}'); }
      catch (e: any) { this.toast.error('Invalid claim_template JSON', e.message); return; }
      const sel = this.editing.sso_portal.scanner_selection;
      sso_portal = {
        ...this.editing.sso_portal,
        required_claims: this.ssoPortalRequiredClaimsText.split(',').map(s => s.trim()).filter(Boolean),
        allowed_memberships: this.ssoPortalAllowedMembershipsText.split(',').map(s => s.trim()).filter(Boolean),
        scanner_selection: sel ? {
          mode: sel.mode,
          scanner_claim: sel.scanner_claim || undefined,
          membership_map: sel.mode === 'from_membership' && Object.keys(membership_map).length > 0 ? membership_map : undefined,
        } : undefined,
        claim_template: Object.keys(claim_template).length > 0 ? claim_template : undefined,
      };
    }

    const payload: any = {
      ...this.editing,
      github: { org: this.editing.github?.org || 'F2Tech-WebDev-v2', repos },
      vercel_projects: vercel,
      scanner_ids: scanners,
      domains,
      urls,
      contacts,
      // Explicit null tells the backend to $unset the sso_portal field
      // entirely. `undefined` would JSON-drop here and the existing
      // sso_portal would silently survive the save.
      sso_portal: sso_portal ?? null,
    };

    if (this.isCreate) {
      if (!payload._id) {
        this.toast.error('Missing slug', 'Customer slug (_id) is required.');
        return;
      }
      const res = await this.svc.create(payload);
      if (res?.err) return;
    } else {
      const res = await this.svc.update(payload._id, payload);
      if (res?.err) return;
    }

    this.showModal = false;
    await this.refresh();
  }

  askDelete(record: CustomerRecord) {
    this.deleteTarget = record;
    this.showDeleteConfirm = true;
  }

  cancelDelete() {
    this.deleteTarget = null;
    this.showDeleteConfirm = false;
  }

  async confirmDelete() {
    if (!this.deleteTarget) return;
    const res = await this.svc.remove(this.deleteTarget._id);
    this.deleteTarget = null;
    this.showDeleteConfirm = false;
    if (!res?.err) await this.refresh();
  }

  // =========================================================
  // Customer Assets (per Portal Settings popup)
  // Backend: f2-admin-service2 /rest/admin/customer-assets/*
  // Files live in F2-ADMIN.CustomerAssets as BinData (<= 700KB),
  // audit trail in F2-ADMIN.CustomerAssetsAudit (HMAC-signed).
  // =========================================================

  private _resetAssetUploadForm() {
    this.psAssetUploadFile = null;
    this.psAssetUploadLabel = '';
    this.psAssetUploadVisibility = { admin: true, client_admin: true, member: true };
    this.psAssetUploadCategory = 'sso-integration';
  }

  private async _loadCustomerAssets(slug: string) {
    this.psAssetsLoading = true;
    try {
      this.psAssets = await this.assetsSvc.list(slug);
    } finally {
      this.psAssetsLoading = false;
    }
  }

  // File picker handler — reads as data URL, strips the
  // `data:<mime>;base64,` prefix, stashes the raw base64 + metadata
  // for the Upload click. SPA-side size guard short-circuits oversize
  // files so we don't burn a multi-MB round trip to learn 413.
  onAssetFilePicked(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > this.ASSET_MAX_BYTES) {
      this.toast.error(
        'File too large',
        `Per-asset cap is ${Math.floor(this.ASSET_MAX_BYTES / 1024)} KB. Selected file is ${Math.ceil(file.size / 1024)} KB.`
      );
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      // dataUrl shape: "data:<mime>;base64,<payload>". Strip the
      // prefix before sending — server takes raw base64.
      const commaIx = dataUrl.indexOf(',');
      const base64 = commaIx >= 0 ? dataUrl.slice(commaIx + 1) : '';
      this.psAssetUploadFile = {
        name: file.name,
        type: file.type || this._guessContentType(file.name),
        size: file.size,
        base64,
      };
      // Pre-fill label with filename so single-click upload still
      // produces a human-readable list row.
      if (!this.psAssetUploadLabel) this.psAssetUploadLabel = file.name;
    };
    reader.onerror = () => {
      this.toast.error('File read failed', String(reader.error?.message || 'unknown error'));
    };
    reader.readAsDataURL(file);
  }

  // Cheap content-type fallback for filenames the browser doesn't
  // type itself (some browsers return '' for .md). Matches the
  // accept="" filter on the file input.
  private _guessContentType(name: string): string {
    const lower = name.toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'text/html';
    if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'text/markdown';
    return 'application/octet-stream';
  }

  // Icon name (font-awesome class fragment) for the content-type
  // column in the asset list. Defaults to a generic file icon.
  assetIcon(contentType: string): string {
    const ct = (contentType || '').toLowerCase();
    if (ct.includes('pdf')) return 'fa-file-pdf text-red-500';
    if (ct.includes('html')) return 'fa-file-code text-orange-500';
    if (ct.includes('markdown')) return 'fa-file-lines text-blue-500';
    return 'fa-file text-gray-400';
  }

  // Human-readable byte size for the list column.
  fmtBytes(n: number): string {
    if (!Number.isFinite(n) || n <= 0) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 102.4) / 10} KB`;
    return `${Math.round(n / (1024 * 102.4)) / 10} MB`;
  }

  // Count of active (non-revoked, non-expired) share tokens — drives
  // the share-token count badge in the list.
  activeShareCount(asset: AssetSummary): number {
    return (asset.share_tokens || []).filter((t) => t.active).length;
  }

  async psUploadAsset() {
    if (!this.portalSettingsRecord?._id) return;
    if (!this.psAssetUploadFile) {
      this.toast.error('No file selected', 'Pick a file before uploading.');
      return;
    }
    const visibility = this.ASSET_VISIBILITY_ROLES.filter((r) => this.psAssetUploadVisibility[r]);
    if (!visibility.length) {
      this.toast.error('Visibility required', 'Pick at least one role.');
      return;
    }
    const slug = this.portalSettingsRecord._id;
    const file = this.psAssetUploadFile;
    const res = await this.assetsSvc.upload({
      customer_slug: slug,
      filename: file.name,
      content_type: file.type,
      label: (this.psAssetUploadLabel || file.name).trim(),
      category: this.psAssetUploadCategory.trim() || 'sso-integration',
      visibility,
      bytes_base64: file.base64,
    });
    if ((res as any)?.err) return;
    const summary = res as AssetSummary;
    // Auto-fill the existing free-text docs_url so the SSO Tester's
    // "Open guide" link resolves to the just-uploaded asset without a
    // second manual step. Only overwrites when sso_portal is enabled —
    // we don't want to silently re-create the sub-object.
    if (this.portalSettingsRecord.sso_portal) {
      this.portalSettingsRecord.sso_portal.docs_url = this.assetsSvc.serveUrl(slug, summary.id);
    }
    this._resetAssetUploadForm();
    await this._loadCustomerAssets(slug);
  }

  async psDeleteAsset(asset: AssetSummary) {
    if (!this.portalSettingsRecord?._id) return;
    if (!confirm(`Delete asset "${asset.label}" (${asset.filename})? This also invalidates every share link for it.`)) return;
    const res = await this.assetsSvc.delete(asset.id);
    if (!res.ok) return;
    // Clear cached audit/share UI for this asset so a re-upload with the
    // same id (won't happen — Mongo OIDs are unique — but defensive)
    // doesn't show stale rows.
    delete this.psAssetAudit[asset.id];
    if (this.psShareFormAssetId === asset.id) this.psShareFormAssetId = null;
    await this._loadCustomerAssets(this.portalSettingsRecord._id);
  }

  // Build the direct-serve URL the operator can open in a new tab to
  // preview / download. Hits the cookie-auth'd serve endpoint; no
  // share-token needed for the operator themself.
  psAssetServeUrl(asset: AssetSummary): string {
    return this.assetsSvc.serveUrl(asset.customer_slug, asset.id);
  }

  // ----- Audit-trail expansion -----
  async psToggleAudit(asset: AssetSummary) {
    const cur = this.psAssetAudit[asset.id];
    if (cur?.open) {
      cur.open = false;
      this.psAssetAudit = { ...this.psAssetAudit };
      return;
    }
    // Open + lazy-load on first expansion.
    const state = cur || { open: false, loaded: false, loading: false, rows: [] as AssetAuditRow[] };
    state.open = true;
    this.psAssetAudit = { ...this.psAssetAudit, [asset.id]: state };
    if (!state.loaded && !state.loading) {
      state.loading = true;
      try {
        state.rows = await this.assetsSvc.audit(asset.customer_slug, asset.id);
        state.loaded = true;
      } finally {
        state.loading = false;
        this.psAssetAudit = { ...this.psAssetAudit, [asset.id]: state };
      }
    }
  }

  // ----- Share-link creation -----
  psOpenShareForm(asset: AssetSummary) {
    this.psShareFormAssetId = asset.id;
    this.psShareFormLabel = '';
    this.psShareFormExpires = '';
  }
  psCloseShareForm() {
    this.psShareFormAssetId = null;
    this.psShareFormLabel = '';
    this.psShareFormExpires = '';
  }

  async psCreateShareLink() {
    if (!this.psShareFormAssetId || !this.portalSettingsRecord?._id) return;
    const label = this.psShareFormLabel.trim();
    if (!label) {
      this.toast.error('Label required', 'Give the share link a label so you can recognize it later.');
      return;
    }
    let expires_at: string | null = null;
    if (this.psShareFormExpires) {
      // <input type="datetime-local"> emits 'yyyy-MM-ddTHH:mm' in local
      // time. Parse via Date() (locale-aware) then re-emit as ISO so the
      // server's ISO parse is unambiguous.
      const d = new Date(this.psShareFormExpires);
      if (isNaN(+d)) {
        this.toast.error('Invalid expiration', 'Pick a valid date/time or leave blank for never.');
        return;
      }
      expires_at = d.toISOString();
    }
    const res = await this.assetsSvc.createShare(this.psShareFormAssetId, { label, expires_at });
    if ((res as any)?.err) return;
    const created = res as AssetShareCreateResponse;
    this.psRevealedShareToken = {
      assetId: this.psShareFormAssetId,
      token: created.token,
      share_url: created.share_url,
      // share_url comes back as a relative path; full URL = apiUrl + path.
      full_url: `${env.apiUrl}${created.share_url}`,
      label,
    };
    this.psCloseShareForm();
    if (this.portalSettingsRecord?._id) await this._loadCustomerAssets(this.portalSettingsRecord._id);
  }

  psCopyShareToken() {
    if (!this.psRevealedShareToken) return;
    navigator.clipboard.writeText(this.psRevealedShareToken.full_url);
    this.toast.success('Copied', 'Share URL copied to clipboard');
  }
  psDismissShareToken() {
    this.psRevealedShareToken = null;
  }

  async psRevokeShareToken(asset: AssetSummary, tokenHash: string) {
    // Server revokes by sha256 hash (the same identifier surfaced in the
    // list). Bearer plaintext is only available at create-time.
    if (!confirm(`Revoke this share link? Recipients will get 404 on next click.`)) return;
    const res = await this.assetsSvc.revokeShare(asset.id, tokenHash);
    if (!res.ok) return;
    if (this.portalSettingsRecord?._id) await this._loadCustomerAssets(this.portalSettingsRecord._id);
  }

  // Resolves docs_url to a fully-qualified URL the operator can click. The
  // backend serves docs_url under the same f2-admin-service2 host the admin
  // SPA already calls, so a leading-slash path needs env.apiUrl prepended;
  // an absolute URL is opened verbatim.
  psDocsUrlPreviewHref(): string {
    const v = String(this.portalSettingsRecord?.sso_portal?.docs_url || '').trim();
    if (!v) return '';
    if (v.startsWith('http://') || v.startsWith('https://')) return v;
    return `${env.apiUrl}${v.startsWith('/') ? '' : '/'}${v}`;
  }

  // Same resolver as psDocsUrlPreviewHref but for the TOP-LEVEL
  // editing.docs_url field on the Customer doc (independent of sso_portal).
  customerDocsUrlPreviewHref(): string {
    const v = String(this.editing?.docs_url || '').trim();
    if (!v) return '';
    if (v.startsWith('http://') || v.startsWith('https://')) return v;
    return `${env.apiUrl}${v.startsWith('/') ? '' : '/'}${v}`;
  }

  // One-click set docs_url to the live-rendered integration-guide endpoint
  // for the currently-edited customer. The rendered HTML pulls scanner enum
  // + redirect host + required claims + email derivation from the same
  // sso_portal config the validator uses, so the doc can never drift from
  // production. Preferred default for any new customer on the generic
  // sso_portal path.
  psUseLiveRenderedGuide() {
    const slug = this.portalSettingsRecord?._id;
    if (!slug || !this.portalSettingsRecord?.sso_portal) return;
    this.portalSettingsRecord.sso_portal.docs_url = `/rest/admin/customer-assets/${slug}/integration-guide`;
  }

  /**
   * Reason string when the live-rendered integration-guide endpoint would 404,
   * empty string when it would render. Backend's _normalize_sso_portal rejects
   * sso_portal shapes with (a) enabled !== true, (b) required_claims.length ===
   * 0, or (c) secrets.length === 0 — clicking Open/Use-live-guide against a
   * rejected shape produces a NOT_FOUND response with the guide URL in the
   * message. Mike caught this 2026-09-14 IT-F2-360 chasing a broken oxc guide:
   * "for god sakes don't let buttons be clicked out a popup requiring that
   * feature be done". Both buttons that point at the live endpoint now gate on
   * this getter with a disabled binding + inline warning surfaces the missing
   * step.
   *
   * Returns empty when docs_url is a hand-curated external URL (starts with
   * http:// or https://) — the check only applies to the live-render endpoint.
   */
  psLiveGuideBlockedReason(): string {
    const docsUrl = String(this.portalSettingsRecord?.sso_portal?.docs_url || '').trim();
    // External URLs bypass the live-render endpoint — no gating applies.
    if (docsUrl.startsWith('http://') || docsUrl.startsWith('https://')) return '';
    const sso = this.portalSettingsRecord?.sso_portal;
    if (!sso) return 'Enable SSO portal handoff first (checkbox at the top of this popup).';
    if (sso.enabled !== true) return 'Enable SSO portal handoff first (checkbox at the top of this popup).';
    const requiredClaims = Array.isArray(sso.required_claims) ? sso.required_claims.filter(Boolean) : [];
    if (requiredClaims.length === 0) return 'Set at least one required claim before the live guide can render.';
    if (this.ssoSecrets.length === 0) return 'Generate a signing secret first — the live guide documents the mint contract, which needs a secret to exist.';
    return '';
  }

  // ----- Customer-level asset list (Customer Directory main edit area) -----
  // SharePoint-style attachments scoped to the editing customer's slug.
  // Reads/writes against the same /rest/admin/customer-assets endpoints the
  // Portal SSO popup uses; that more-detailed surface stays available for
  // share-links + audit + visibility editing. This UI just covers the
  // common case (add / list / open / copy URL / delete) with a role-
  // visibility pill row per asset for at-a-glance auditability.
  customerAssets: AssetSummary[] = [];
  customerAssetsLoading = false;
  customerAssetUploading = false;
  customerAssetUploadFile: File | null = null;

  private async _loadCustomerAssetsForEdit(slug: string) {
    this.customerAssetsLoading = true;
    try {
      this.customerAssets = await this.assetsSvc.list(slug);
    } catch {
      this.customerAssets = [];
    } finally {
      this.customerAssetsLoading = false;
    }
  }

  customerAssetServeUrl(asset: AssetSummary): string {
    return `${env.apiUrl}/rest/admin/customer-assets/${asset.customer_slug}/${asset.id}`;
  }

  async copyCustomerAssetUrl(asset: AssetSummary) {
    const url = this.customerAssetServeUrl(asset);
    try {
      await navigator.clipboard.writeText(url);
      this.toast.success('Copied', 'Asset URL copied to clipboard');
    } catch {
      this.toast.error('Copy failed', 'Clipboard write was rejected');
    }
  }

  onCustomerAssetFilePicked(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.customerAssetUploadFile = file || null;
  }

  async uploadCustomerAsset() {
    if (!this.customerAssetUploadFile || !this.editing?._id) return;
    const slug = this.editing._id;
    const file = this.customerAssetUploadFile;
    // Pre-flight the 700KB cap so we don't burn a request the server 413s.
    const MAX = 700 * 1024;
    if (file.size > MAX) {
      this.toast.error('File too large', `${file.name} is ${file.size} bytes; cap is ${MAX} bytes (~700KB).`);
      return;
    }
    this.customerAssetUploading = true;
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const dataUrl = String(reader.result || '');
          const idx = dataUrl.indexOf(',');
          resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const created = await this.assetsSvc.upload({
        customer_slug: slug,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        label: file.name,
        category: 'sso-integration',
        visibility: ['admin', 'client_admin', 'member'],
        bytes_base64: base64,
      });
      if (created && !('err' in created)) {
        this.customerAssets = [...this.customerAssets, created];
        this.customerAssetUploadFile = null;
      }
    } catch (e: any) {
      this.toast.error('Upload failed', e?.message || 'Unknown error');
    } finally {
      this.customerAssetUploading = false;
    }
  }

  async deleteCustomerAsset(asset: AssetSummary) {
    if (!confirm(`Delete asset "${asset.label || asset.filename}"? An audit entry records the removal.`)) return;
    const res = await this.assetsSvc.delete(asset.id);
    if (!res?.ok) return;
    this.customerAssets = this.customerAssets.filter(a => a.id !== asset.id);
    // If the deleted asset was wired to the top-level docs_url, clear that
    // pointer so the operator doesn't ship a broken link.
    const servePath = `/rest/admin/customer-assets/${asset.customer_slug}/${asset.id}`;
    if (this.editing?.docs_url === servePath) {
      this.editing.docs_url = '';
    }
  }
}
